import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, Sparkles, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'caferuis-bc1';

const theme = {
  primary: '#2A9D8F',
  secondary: '#E75480',
  bg: '#F0FDF9',
  text: '#1a2e35',
};

const bgImage = "https://customer-assets.emergentagent.com/job_9e595d24-b8ef-429a-8a8c-c23583fe1376/artifacts/uwszpqa9_image.png";

const slides = [
  // SLIDE 0 — TITRE
  {
    type: 'title',
    title: "Bloc 1",
    subtitle: "Piloter l'activité d'une unité d'intervention sociale",
    narration: "Bienvenue dans le Bloc 1 du CAFERUIS : Piloter l'activité d'une unité d'intervention sociale. Ce bloc couvre l'organisation, la qualité, l'évaluation, les partenariats, le travail en réseau et la communication managériale."
  },
  // SLIDE 1 — OBJECTIFS GÉNÉRAUX
  {
    type: 'content',
    title: "Objectifs généraux de la certification",
    points: [
      "Acquérir et démontrer une posture de cadre intermédiaire et de responsable d'unité d'intervention sociale",
      "Maîtriser les outils de pilotage de l'activité et de la qualité dans une logique d'amélioration continue",
      "Développer des compétences managériales adaptées à l'animation et à la coordination d'équipes pluridisciplinaires",
      "Assurer une gestion administrative, logistique et budgétaire de l'unité ou du service",
      "Contribuer à la mise en œuvre et à l'évolution du projet d'établissement ou de service, en lien avec les orientations institutionnelles et les politiques sociales"
    ],
    highlight: "Certificat d'Aptitude aux Fonctions d'Encadrement et de Responsable d'Unité d'Intervention Sociale — Formation sur 18 mois — Référentiel 2022",
    narration: "La certification CAFERUIS vise à permettre au candidat d'acquérir une posture de cadre intermédiaire, de maîtriser les outils de pilotage, de développer des compétences managériales, d'assurer la gestion administrative et budgétaire, et de contribuer au projet d'établissement."
  },
  // SLIDE 2 — ORGANISATION DE L'ACCOMPAGNEMENT
  {
    type: 'content',
    title: "Organisation de l'accompagnement",
    points: [
      "Des entretiens individuels",
      "Des temps d'analyse de pratique",
      "Des apports méthodologiques sur le référentiel",
      "Un travail d'écriture accompagné",
      "Des relectures et ajustements du dossier",
      "Une préparation à la soutenance orale"
    ],
    highlight: "L'accompagnement s'appuie sur une pédagogie active centrée sur l'expérience du candidat. L'objectif est de permettre au candidat de mettre en évidence ses compétences de cadre intermédiaire et de responsable d'unité d'intervention sociale à partir de situations professionnelles réelles.",
    narration: "L'accompagnement comprend des entretiens individuels, des temps d'analyse de pratique, des apports méthodologiques, un travail d'écriture accompagné, des relectures et une préparation à la soutenance orale."
  },
  // SLIDE 3 — SOMMAIRE GÉNÉRAL
  {
    type: 'sommaire',
    title: "Sommaire du Bloc 1",
    sections: [
      { num: "1", title: "Chapitre 1 — Organiser la mise en œuvre de l'activité", targetSlide: 5 },
      { num: "2", title: "Chapitre 2 — Assurer la qualité des projets individuels et collectifs", targetSlide: 52 },
      { num: "3", title: "Chapitre 3 — Évaluer les résultats de la mise en œuvre de l'activité", targetSlide: 85 },
      { num: "4", title: "Chapitre 4 — Mettre en place un partenariat au service de l'activité", targetSlide: 121 },
      { num: "5", title: "Chapitre 5 — Impulser une dynamique de travail en réseau", targetSlide: 158 },
      { num: "6", title: "Chapitre 6 — Communiquer dans le cadre du pilotage", targetSlide: 190 }
    ],
    narration: "Le Bloc 1 couvre 6 chapitres : l'organisation de l'activité, la qualité des projets, l'évaluation des résultats, les partenariats, le travail en réseau et la communication dans le pilotage."
  },
  // SLIDE 4 — SOMMAIRE DÉTAILLÉ CHAPITRE 1
  {
    type: 'grid',
    title: "Chapitre 1 – Organiser la mise en œuvre de l'activité",
    items: [
      { num: "1", label: "Les fondamentaux de l'organisation d'une UIS" },
      { num: "2", label: "L'approche systémique appliquée aux organisations sociales" },
      { num: "3", label: "Les acteurs d'une unité d'intervention sociale" },
      { num: "4", label: "Les organigrammes hiérarchiques et fonctionnels" },
      { num: "5", label: "Les flux d'information dans une organisation" },
      { num: "6", label: "La planification de l'activité" },
      { num: "7", label: "Les outils de gestion et de suivi de l'activité" },
      { num: "8", label: "La gestion des priorités et la prise de décision" },
      { num: "9", label: "Adapter l'organisation aux besoins des usagers" }
    ],
    narration: "Le Chapitre 1 couvre 9 thèmes : les fondamentaux de l'organisation, l'approche systémique, les acteurs, les organigrammes, les flux d'information, la planification, les outils de gestion, la gestion des priorités et l'adaptation aux besoins des usagers."
  },

  // CHAPITRE 1 — ORGANISER LA MISE EN ŒUVRE DE L'ACTIVITÉ
  // SLIDE 5
  { type: 'section', title: "Chapitre 1", subtitle: "Organiser la mise en œuvre de l'activité",
    narration: "Chapitre 1 : Organiser la mise en œuvre de l'activité d'une unité d'intervention sociale." },
  // SLIDE 6
  { type: 'content', title: "Objectifs pédagogiques du Chapitre 1", points: [
    "Comprendre les principes d'organisation d'une unité d'intervention sociale",
    "Structurer l'activité d'un service ou d'une unité",
    "Planifier et prioriser les actions quotidiennes et stratégiques",
    "Adapter l'organisation aux besoins évolutifs des usagers et aux contraintes institutionnelles"
  ], highlight: "L'organisation vise à garantir la continuité de l'accompagnement, la qualité des interventions, la cohérence des actions professionnelles et la sécurité des usagers.",
    narration: "Les objectifs pédagogiques du Chapitre 1 visent à comprendre les principes d'organisation, structurer l'activité, planifier et prioriser les actions, et adapter l'organisation aux besoins des usagers." },
  // SLIDE 7 — SOUS-SECTION
  { type: 'section', title: "1. Les fondamentaux de l'organisation d'une unité d'intervention sociale",
    narration: "Les fondamentaux de l'organisation d'une unité d'intervention sociale." },
  // SLIDE 8 — DÉFINITION UIS
  { type: 'content', title: "Définition",
    intro: "Une unité d'intervention sociale (UIS) peut être définie comme :",
    highlight: "Un ensemble organisé de moyens humains, matériels et organisationnels visant à répondre aux besoins d'un public spécifique dans un cadre institutionnel donné.",
    narration: "Une unité d'intervention sociale peut être définie comme un ensemble organisé de moyens humains, matériels et organisationnels visant à répondre aux besoins d'un public spécifique dans un cadre institutionnel donné." },
  // SLIDE 9 — CARACTÉRISTIQUES
  { type: 'content', title: "Les caractéristiques d'une organisation sociale",
    intro: "Une UIS se caractérise par :",
    points: [
    "Une mission sociale ou médico-sociale",
    "Une organisation structurée",
    "Des professionnels aux compétences variées",
    "Des interactions constantes avec l'environnement"
  ], narration: "Une UIS se caractérise par une mission sociale ou médico-sociale, une organisation structurée, des professionnels aux compétences variées et des interactions constantes avec l'environnement." },
  // SLIDE 10 — FONCTIONS
  { type: 'content', title: "Les fonctions de l'organisation",
    intro: "Toute organisation repose sur plusieurs fonctions :",
    points: [
    "Organiser les ressources",
    "Coordonner les actions",
    "Garantir la continuité du service",
    "Répondre aux besoins des usagers"
  ], highlight: "Le rôle du cadre est de structurer ces fonctions pour assurer l'efficacité du service.",
    narration: "Toute organisation repose sur plusieurs fonctions : organiser les ressources, coordonner les actions, garantir la continuité du service et répondre aux besoins des usagers." },
  // SLIDE 11
  { type: 'content', title: "Définition de l'organisation",
    intro: "L'organisation peut être définie comme l'ensemble des moyens humains, matériels et procéduraux mobilisés pour atteindre des objectifs communs.",
    intro2: "Dans le secteur social et médico-social, l'organisation vise à garantir :",
    points: [
    "La continuité de l'accompagnement",
    "La qualité des interventions",
    "La cohérence des actions professionnelles",
    "La sécurité des usagers"
  ], highlight: "Une organisation efficace permet de répondre aux besoins des publics tout en respectant les contraintes institutionnelles et réglementaires.",
    narration: "L'organisation peut être définie comme l'ensemble des moyens humains, matériels et procéduraux mobilisés pour atteindre des objectifs communs. Dans le secteur social et médico-social, l'organisation vise à garantir la continuité de l'accompagnement, la qualité des interventions, la cohérence des actions professionnelles et la sécurité des usagers." },
  // SLIDE 8 — SOUS-SECTION
  { type: 'section', title: "2. L'approche systémique appliquée aux organisations sociales",
    narration: "L'approche systémique appliquée aux organisations sociales." },
  // SLIDE 9
  { type: 'content', title: "L'approche systémique",
    intro: "L'approche systémique est une méthode d'analyse qui considère qu'une organisation est composée d'éléments interdépendants formant un système.",
    intro2: "Un système est défini comme : un ensemble d'éléments en interaction organisés autour d'un objectif commun.",
    intro3: "Dans une unité d'intervention sociale, ces éléments sont notamment :",
    points: [
    "Les professionnels",
    "Les usagers",
    "Les règles institutionnelles",
    "Les partenaires",
    "Les ressources matérielles"
  ], highlight: "Chaque élément influence les autres. Ainsi, une modification dans une partie du système peut entraîner des conséquences sur l'ensemble de l'organisation.",
    narration: "L'approche systémique est une méthode d'analyse qui considère qu'une organisation est composée d'éléments interdépendants formant un système. Un système est défini comme un ensemble d'éléments en interaction organisés autour d'un objectif commun. Chaque élément influence les autres." },
  // SLIDE — APPLICATION AU SECTEUR SOCIAL
  { type: 'content', title: "Application au secteur social",
    intro: "Dans une UIS :",
    points: [
    "Une décision organisationnelle impacte les équipes",
    "Une modification des pratiques impacte les usagers",
    "Une contrainte budgétaire impacte l'organisation"
  ], intro2: "L'approche systémique permet au cadre de :",
    points2: [
    "Anticiper les conséquences de ses décisions",
    "Comprendre les interactions entre les acteurs",
    "Ajuster l'organisation de manière globale"
  ], narration: "Dans une UIS, une décision organisationnelle impacte les équipes, une modification des pratiques impacte les usagers, une contrainte budgétaire impacte l'organisation. L'approche systémique permet au cadre d'anticiper, comprendre et ajuster." },
  // SLIDE 10 — EXEMPLE CONCRET
  { type: 'content', title: "Exemple concret",
    intro: "Dans un service éducatif :",
    points: [
    "Un changement d'organisation des horaires peut modifier la disponibilité des professionnels",
    "Cela peut impacter la qualité de l'accompagnement",
    "Ce changement peut générer des tensions dans l'équipe"
  ], highlight: "Le responsable d'unité doit donc adopter une vision globale du fonctionnement du service.",
    narration: "Exemple concret : dans un service éducatif, un changement d'organisation des horaires peut modifier la disponibilité des professionnels, impacter la qualité de l'accompagnement et générer des tensions dans l'équipe. Le responsable d'unité doit adopter une vision globale." },
  // SLIDE 11 — SOUS-SECTION
  { type: 'section', title: "3. Les acteurs d'une unité d'intervention sociale",
    narration: "Les acteurs d'une unité d'intervention sociale." },
  // SLIDE 18
  { type: 'content', title: "Les professionnels",
    intro: "Ils interviennent directement auprès des usagers :",
    points: [
      "Éducateurs spécialisés et moniteurs éducateurs",
      "Assistants de service social",
      "Infirmiers et aides-soignants",
      "Psychologues et psychomotriciens",
      "Agents administratifs et logistiques"
    ],
    highlight: "Chaque professionnel apporte une expertise spécifique. La complémentarité des compétences constitue une richesse pour l'accompagnement.",
    narration: "Les professionnels interviennent directement auprès des usagers : éducateurs, assistants sociaux, infirmiers, psychologues et agents administratifs. Leur complémentarité est une richesse." },
  // SLIDE 19
  { type: 'content', title: "L'encadrement intermédiaire",
    intro: "Le responsable d'unité occupe une position centrale dans l'organisation. Il assure l'interface entre la direction et les équipes de terrain.",
    intro2: "Ses missions principales :",
    points: [
      "Organiser le travail et planifier les activités",
      "Coordonner les interventions et garantir leur cohérence",
      "Accompagner les équipes dans leur pratique professionnelle",
      "Piloter les projets personnalisés et collectifs",
      "Rendre compte de l'activité à la direction"
    ],
    narration: "Le responsable d'unité occupe une position centrale. Il organise, coordonne, accompagne les équipes, pilote les projets et rend compte à la direction." },
  // SLIDE 20
  { type: 'content', title: "La direction",
    intro: "La direction est responsable de la stratégie globale de l'établissement.",
    intro2: "Elle définit :",
    points: [
      "Les orientations stratégiques et les projets d'établissement",
      "Les objectifs institutionnels et les moyens alloués",
      "Les partenariats avec les autorités de tutelle et de tarification"
    ],
    highlight: "Le cadre intermédiaire doit traduire ces orientations en actions concrètes au sein de son unité.",
    narration: "La direction définit les orientations stratégiques, les objectifs institutionnels et les partenariats. Le cadre intermédiaire traduit ces orientations en actions concrètes." },
  // SLIDE 21
  { type: 'content', title: "Les partenaires extérieurs",
    intro: "Les partenaires participent à l'accompagnement global des usagers :",
    points: [
      "Institutions publiques (ASE, MDPH, ARS, conseil départemental)",
      "Associations et structures du secteur social",
      "Services de santé et médico-sociaux",
      "Établissements scolaires et de formation",
      "Services judiciaires (PJJ, juges des enfants)"
    ],
    highlight: "Le développement de partenariats est essentiel pour apporter une réponse complète aux besoins des usagers.",
    narration: "Les partenaires participent à l'accompagnement : institutions publiques, associations, services de santé, établissements scolaires et services judiciaires." },
  // SLIDE 22
  { type: 'content', title: "Les usagers et leurs familles",
    intro: "Les usagers sont au cœur du dispositif d'accompagnement.",
    intro2: "Leur place dans l'organisation implique :",
    points: [
      "Le respect de leurs droits fondamentaux (loi 2002-2)",
      "La participation à l'élaboration de leur projet personnalisé",
      "L'expression de leurs besoins et attentes",
      "L'implication de leurs familles dans le processus d'accompagnement"
    ],
    highlight: "L'organisation doit être construite en fonction de leurs besoins, et non l'inverse.",
    narration: "Les usagers sont au cœur du dispositif. Leur place implique le respect de leurs droits, la participation à leur projet, l'expression de leurs besoins et l'implication des familles." },
  // SLIDE 23 — SOUS-SECTION
  { type: 'section', title: "4. Les organigrammes hiérarchiques et fonctionnels",
    narration: "Les organigrammes hiérarchiques et fonctionnels." },
  // SLIDE 24
  { type: 'content', title: "Définition de l'organigramme",
    intro: "Un organigramme est une représentation graphique de la structure d'une organisation.",
    intro2: "Il permet de visualiser :",
    points: [
    "Les responsabilités",
    "Les niveaux hiérarchiques",
    "Les fonctions",
    "Les relations entre les postes"
  ], narration: "Un organigramme est une représentation graphique de la structure d'une organisation. Il permet de visualiser les responsabilités, les niveaux hiérarchiques, les fonctions et les relations entre les postes." },
  // SLIDE 25 — ORGANIGRAMME HIÉRARCHIQUE
  { type: 'orgchart', title: "Organigramme hiérarchique (exemple)",
    points: [
    "Qui prend les décisions",
    "Qui encadre qui",
    "Comment s'organise l'autorité"
  ], narration: "Cet organigramme hiérarchique montre la chaîne de commandement : la Direction, le Responsable d'unité, puis les professionnels comme les éducateurs, psychologues et infirmiers, et enfin les AES et moniteurs éducateurs." },
  // SLIDE 26 — ORGANIGRAMME FONCTIONNEL
  { type: 'orgchart_func', title: "Organigramme fonctionnel",
    intro: "L'organigramme fonctionnel met en évidence les fonctions et les interactions professionnelles.",
    points: [
    "Les missions",
    "Les domaines d'expertise",
    "Les collaborations professionnelles"
  ], narration: "L'organigramme fonctionnel met en évidence les fonctions et les interactions professionnelles. Il permet de mieux comprendre les missions, les domaines d'expertise et les collaborations professionnelles." },
  // SLIDE 11
  { type: 'table', title: "Les organigrammes dans l'organisation du service",
    rows: [
      { situation: "Organigramme hiérarchique", besoin: "Visualise les responsabilités, niveaux hiérarchiques, fonctions et relations d'autorité" },
      { situation: "Organigramme fonctionnel", besoin: "Met en évidence les missions, domaines d'expertise et collaborations professionnelles" },
      { situation: "Qui prend les décisions ?", besoin: "L'organigramme hiérarchique montre la chaîne de commandement" },
      { situation: "Qui collabore avec qui ?", besoin: "L'organigramme fonctionnel montre les interactions professionnelles" }
    ], narration: "L'organigramme hiérarchique permet de comprendre qui prend les décisions et comment s'organise l'autorité. L'organigramme fonctionnel met en évidence les missions et les collaborations." },
  // SLIDE 28 — SOUS-SECTION
  { type: 'section', title: "5. Les flux d'information dans une organisation",
    narration: "Les flux d'information dans une organisation." },
  // SLIDE 29 — DÉFINITION FLUX + SCHÉMA
  { type: 'flux_schema', title: "Les flux d'information — Définition",
    intro: "Les flux d'information correspondent à la circulation des informations au sein d'une organisation.",
    intro2: "Une circulation efficace de l'information est essentielle pour :",
    points: [
    "Coordonner les interventions",
    "Assurer la continuité de l'accompagnement",
    "Éviter les erreurs",
    "Maintenir la cohérence des actions"
  ], narration: "Les flux d'information correspondent à la circulation des informations au sein d'une organisation. Une circulation efficace est essentielle pour coordonner, assurer la continuité, éviter les erreurs et maintenir la cohérence." },
  // SLIDE 30 — FLUX DESCENDANTS
  { type: 'content', title: "Les flux descendants",
    intro: "Les flux descendants correspondent aux informations transmises de la direction vers les équipes.",
    intro2: "Exemples :",
    points: [
    "Orientations stratégiques",
    "Décisions institutionnelles",
    "Consignes professionnelles",
    "Projets de service"
  ], narration: "Les flux descendants correspondent aux informations transmises de la direction vers les équipes : orientations stratégiques, décisions institutionnelles, consignes professionnelles et projets de service." },
  // SLIDE 30 — FLUX ASCENDANTS
  { type: 'content', title: "Les flux ascendants",
    intro: "Les flux ascendants correspondent aux informations remontant des équipes vers la hiérarchie :",
    points: [
    "Difficultés rencontrées",
    "Besoins des usagers",
    "Propositions d'amélioration",
    "Observations professionnelles"
  ], highlight: "Ces remontées sont essentielles pour adapter l'organisation.",
    narration: "Les flux ascendants correspondent aux informations remontant des équipes vers la hiérarchie : difficultés rencontrées, besoins des usagers, propositions d'amélioration et observations professionnelles." },
  // SLIDE 31 — FLUX TRANSVERSAUX
  { type: 'content', title: "Les flux transversaux",
    intro: "Les flux transversaux correspondent aux échanges entre professionnels :",
    points: [
    "Réunions de synthèse",
    "Réunions d'équipe",
    "Groupes de travail"
  ], highlight: "Ils permettent la coordination des actions et la coopération pluridisciplinaire.",
    narration: "Les flux transversaux correspondent aux échanges entre professionnels : réunions de synthèse, réunions d'équipe et groupes de travail. Ils permettent la coordination des actions." },
  // SLIDE 32 — ENJEUX
  { type: 'content', title: "Les enjeux de la circulation de l'information",
    intro: "Une mauvaise circulation de l'information peut entraîner :",
    points: [
      "Des erreurs dans l'accompagnement des usagers",
      "Des tensions et incompréhensions au sein de l'équipe",
      "Une perte de qualité des interventions",
      "Des doublons ou des oublis dans les actions menées",
      "Un isolement professionnel de certains acteurs"
    ],
    highlight: "La qualité de l'information conditionne directement la qualité de l'accompagnement.",
    narration: "Une mauvaise circulation de l'information peut entraîner des erreurs, des tensions, une perte de qualité, des doublons et un isolement professionnel." },
  // SLIDE 33 — RÔLE DU CADRE
  { type: 'content', title: "Le rôle du cadre dans la gestion de l'information",
    intro: "Le cadre doit veiller à une circulation efficace de l'information en :",
    points: [
      "Structurant les circuits d'information (qui communique quoi, à qui, comment)",
      "Garantissant la fiabilité et la traçabilité des informations transmises",
      "Organisant les temps de transmission (réunions, cahier de liaison, transmissions)",
      "Choisissant les supports adaptés selon la nature de l'information",
      "Respectant le cadre légal (secret professionnel, partage d'informations à caractère secret)"
    ],
    narration: "Le cadre doit structurer les circuits d'information, garantir la fiabilité, organiser les temps de transmission et respecter le cadre légal." },
  // SLIDE — SOUS-SECTION
  { type: 'section', title: "6. La planification de l'activité",
    narration: "La planification de l'activité." },
  // SLIDE 35
  { type: 'content', title: "La planification de l'activité",
    intro: "La planification consiste à organiser les actions dans le temps afin d'atteindre les objectifs fixés.",
    intro2: "Elle permet :",
    points: [
    "D'anticiper les besoins",
    "De répartir les tâches",
    "De coordonner les interventions"
  ], narration: "La planification consiste à organiser les actions dans le temps. Elle permet d'anticiper les besoins, de répartir les tâches et de coordonner les interventions." },
  // SLIDE 36 — DIAGRAMME DE GANTT
  { type: 'gantt', title: "Le diagramme de Gantt",
    intro: "Le diagramme de Gantt est un outil de planification visuelle.",
    highlight: "Il permet de visualiser l'avancement des actions.",
    narration: "Le diagramme de Gantt est un outil de planification visuelle. Il permet de visualiser l'avancement des actions dans le temps." },
  // SLIDE 38 — RÉTROPLANNING
  { type: 'content', title: "Le rétroplanning",
    intro: "Le rétroplanning est une méthode de planification inversée : on part de la date d'échéance finale et on remonte dans le temps pour identifier chaque étape nécessaire.",
    intro2: "Cette méthode permet de :",
    points: [
    "Définir les délais réalistes pour chaque action",
    "Identifier les étapes critiques à ne pas dépasser",
    "Anticiper les contraintes (disponibilité des équipes, délais administratifs)",
    "Prioriser les actions en fonction de l'objectif final"
  ], highlight: "Exemple : pour préparer un projet de service à rendre en juin, le rétroplanning fixe la rédaction finale en mai, la validation en avril, les consultations en mars et le diagnostic en février.",
    narration: "Le rétroplanning est une méthode de planification inversée. On part de la date d'échéance finale et on remonte dans le temps pour identifier chaque étape nécessaire." },
  // SLIDE 39 — ENJEUX PLANIFICATION
  { type: 'content', title: "Enjeux de la planification",
    intro: "Une planification rigoureuse permet de :",
    points: [
    "Assurer la continuité du service",
    "Optimiser les ressources",
    "Anticiper les contraintes"
  ], narration: "Les enjeux de la planification sont d'assurer la continuité du service, d'optimiser les ressources et d'anticiper les contraintes." },
  // SLIDE 40 — SOUS-SECTION
  { type: 'section', title: "7. Les outils de gestion et de suivi de l'activité",
    narration: "Les outils de gestion et de suivi de l'activité." },
  // SLIDE 41 — TABLEAUX DE BORD
  { type: 'dashboard', title: "Les tableaux de bord",
    intro: "Le tableau de bord est un outil de pilotage permettant de suivre l'activité du service. Il comprend des indicateurs.",
    quantitatifs: [
    "Nombre d'usagers accompagnés",
    "Taux d'occupation de l'établissement",
    "Nombre d'actions éducatives réalisées"
  ], qualitatifs: [
    "Satisfaction des usagers",
    "Qualité des projets personnalisés",
    "Évolution de l'autonomie des personnes"
  ], highlight: "Le tableau de bord permet de visualiser les résultats, détecter les difficultés, faciliter la prise de décision et mesurer l'atteinte des objectifs.",
    narration: "Le tableau de bord est un outil de pilotage. Il comprend des indicateurs quantitatifs comme le nombre d'usagers et le taux d'occupation, et des indicateurs qualitatifs comme la satisfaction et la qualité des projets." },
  // SLIDE 15
  { type: 'section', title: "8. La gestion des priorités et la prise de décision",
    narration: "La gestion des priorités et la prise de décision." },
  // SLIDE 43
  { type: 'content', title: "La gestion des priorités",
    intro: "Le responsable d'unité est quotidiennement confronté à de multiples sollicitations. Il doit savoir arbitrer entre :",
    points: [
      "L'urgence — ce qui demande une réponse immédiate (incident, situation de crise)",
      "L'importance — ce qui contribue aux objectifs à long terme (projets, évaluation, formation)"
    ],
    highlight: "La confusion entre urgence et importance est l'un des pièges les plus fréquents dans l'encadrement. Tout ce qui est urgent n'est pas forcément important, et inversement.",
    narration: "Le cadre doit arbitrer entre l'urgence et l'importance. La confusion entre les deux est un piège fréquent dans l'encadrement." },
  // SLIDE 44 — MATRICE EISENHOWER VISUELLE
  { type: 'eisenhower', title: "La matrice d'Eisenhower",
    points: [
    "L'urgent",
    "L'important"
  ], narration: "La matrice d'Eisenhower aide à classer les tâches selon leur urgence et leur importance : faire immédiatement, planifier, déléguer ou éliminer." },
  // SLIDE 45
  { type: 'content', title: "La prise de décision",
    intro: "La prise de décision est une compétence essentielle du cadre. Elle implique :",
    points: [
      "Analyser la situation en recueillant les informations pertinentes",
      "Identifier les différentes options possibles",
      "Évaluer les risques et les conséquences de chaque option",
      "Choisir la solution la plus adaptée au contexte",
      "Communiquer et expliquer la décision à l'équipe"
    ],
    highlight: "Toute décision doit pouvoir être justifiée au regard des objectifs du service, des besoins des usagers et du cadre réglementaire.",
    narration: "La prise de décision implique d'analyser la situation, identifier les options, évaluer les risques, choisir et communiquer. Toute décision doit être justifiable." },
  // SLIDE 46
  { type: 'content', title: "Les responsabilités du cadre dans la décision",
    intro: "Le cadre doit :",
    points: [
      "Assumer pleinement les décisions prises, même en situation d'incertitude",
      "Expliquer les choix à l'équipe de manière transparente",
      "Impliquer les professionnels dans la réflexion lorsque c'est possible",
      "Ajuster les décisions si le contexte évolue",
      "Documenter les décisions importantes pour en assurer la traçabilité"
    ],
    highlight: "La légitimité du cadre repose en grande partie sur sa capacité à prendre des décisions cohérentes et à les assumer.",
    narration: "Le cadre doit assumer les décisions, expliquer les choix, impliquer les professionnels, ajuster si nécessaire et documenter. Sa légitimité repose sur cette capacité." },
  // SLIDE — SOUS-SECTION
  { type: 'section', title: "9. Adapter l'organisation aux besoins des usagers",
    narration: "Adapter l'organisation aux besoins des usagers." },
  // SLIDE 48
  { type: 'content', title: "Adapter l'organisation aux besoins des usagers",
    intro: "L'organisation doit rester centrée sur les besoins des publics accompagnés. Elle ne doit jamais devenir une fin en soi.",
    intro2: "Cela implique pour le responsable d'unité :",
    points: [
      "L'analyse régulière de la demande sociale et de l'évolution des besoins",
      "La flexibilité organisationnelle pour s'adapter aux situations nouvelles",
      "La personnalisation de l'accompagnement (projets personnalisés)",
      "L'écoute des usagers et de leurs familles dans l'organisation du service",
      "La veille sur les évolutions législatives et les recommandations professionnelles (HAS, RBPP)"
    ],
    highlight: "Une organisation qui ne s'adapte pas aux besoins de son public risque de perdre son sens et sa pertinence.",
    narration: "L'organisation doit rester centrée sur les besoins des usagers. Cela implique l'analyse de la demande, la flexibilité, la personnalisation, l'écoute et la veille professionnelle." },
  // SLIDE 49
  { type: 'jurybox', title: "À retenir — Chapitre 1",
    intro: "Le responsable d'unité doit :",
    points: [
      "Comprendre le fonctionnement global du service (approche systémique)",
      "Structurer l'organisation et les circuits de décision",
      "Coordonner les professionnels et les partenaires",
      "Assurer une circulation fiable de l'information",
      "Planifier l'activité (Gantt, rétroplanning) et gérer les priorités (Eisenhower)",
      "Adapter continuellement l'organisation aux besoins des usagers"
    ], highlight: "Ces compétences sont essentielles pour garantir la qualité de l'accompagnement dans le secteur social et médico-social.",
    narration: "Le responsable d'unité doit comprendre le fonctionnement global, structurer l'organisation, coordonner les acteurs, assurer la circulation de l'information, planifier et adapter l'organisation aux besoins des usagers." },
  // SLIDE 50 — SYNTHÈSE CHAPITRE 1
  { type: 'jurybox', title: "Synthèse du Chapitre 1", points: [
    "Méthodologie de planification : Outils de planification (Gantt, rétro-planning), gestion des temps et des délais",
    "Outils de gestion et de suivi : Tableaux de bord d'activité, indicateurs quantitatifs et qualitatifs",
    "Gestion des priorités : Matrice d'Eisenhower, arbitrage entre urgence et importance, délégation",
    "Adaptation aux besoins des usagers : Analyse de la demande sociale, flexibilité organisationnelle, personnalisation de l'offre"
  ], narration: "Synthèse du Chapitre 1 : méthodologie de planification avec Gantt et rétro-planning, outils de gestion et tableaux de bord, gestion des priorités avec Eisenhower, et adaptation aux besoins des usagers." },

  // SLIDE 51 — Transition Chapitre 2
  { type: 'section', title: "Chapitre 2", subtitle: "Assurer la qualité des projets individuels et collectifs",
    narration: "Chapitre 2 : Assurer la qualité des projets individuels et collectifs." },
  // SLIDE — SOMMAIRE CHAPITRE 2
  { type: 'sommaire', title: "Sommaire du Chapitre 2",
    sections: [
      { num: "1", title: "La démarche qualité dans le secteur social et médico-social", targetSlide: 56 },
      { num: "2", title: "Les principes de l'amélioration continue", targetSlide: 57 },
      { num: "3", title: "La culture de la bientraitance", targetSlide: 62 },
      { num: "4", title: "La méthodologie d'élaboration des projets personnalisés", targetSlide: 64 },
      { num: "5", title: "La co-construction avec l'usager", targetSlide: 71 },
      { num: "6", title: "L'évaluation des projets personnalisés", targetSlide: 73 },
      { num: "7", title: "Les projets collectifs et la dynamique de groupe", targetSlide: 78 }
    ],
    narration: "Le Chapitre 2 couvre 7 thèmes : la démarche qualité, l'amélioration continue, la bientraitance, les projets personnalisés, la co-construction, l'évaluation et les projets collectifs." },
  // SLIDE 52 — INTRODUCTION CHAPITRE 2
  { type: 'content', title: "Introduction",
    intro: "Dans le secteur social et médico-social, la qualité de l'accompagnement repose en grande partie sur la capacité des professionnels à concevoir, mettre en œuvre et évaluer des projets adaptés aux besoins des usagers.",
    intro3: "Ces projets peuvent être :",
    points: [
    "Individuels, lorsqu'ils concernent l'accompagnement personnalisé d'une personne",
    "Collectifs, lorsqu'ils visent à favoriser la participation sociale et la dynamique de groupe"
  ], intro2: "Le responsable d'unité doit veiller à ce que les pratiques soient cohérentes avec :",
    points2: [
    "Les besoins des usagers",
    "Les orientations institutionnelles",
    "Les recommandations de bonnes pratiques",
    "Le cadre réglementaire du secteur social et médico-social"
  ], highlight: "La démarche qualité constitue ainsi un levier essentiel pour améliorer en continu les pratiques professionnelles et renforcer la pertinence des interventions.",
    narration: "Dans le secteur social et médico-social, la qualité repose sur la capacité à concevoir des projets adaptés. Ces projets peuvent être individuels ou collectifs. Le responsable d'unité veille à la cohérence avec les besoins, les orientations, les bonnes pratiques et le cadre réglementaire." },

  // CHAPITRE 2 — ASSURER LA QUALITÉ DES PROJETS
  // SLIDE 53
  { type: 'content', title: "Objectifs pédagogiques du Chapitre 2", points: [
    "Comprendre les principes de la démarche qualité dans le secteur social et médico-social",
    "Mobiliser les outils d'amélioration continue",
    "Garantir la cohérence et la qualité des projets personnalisés",
    "Soutenir la mise en œuvre de projets collectifs favorisant la participation des usagers",
    "Intégrer les référentiels de bonnes pratiques dans l'organisation du service"
  ], highlight: "La qualité ne se limite pas à l'efficacité des interventions. Elle concerne également le respect des droits des usagers, la pertinence de l'accompagnement, la participation des personnes accompagnées et l'amélioration des pratiques professionnelles.",
    narration: "Les objectifs du Chapitre 2 couvrent la démarche qualité, les outils d'amélioration continue, les projets personnalisés, les projets collectifs et les référentiels de bonnes pratiques." },
  // SLIDE 54 — SOUS-SECTION
  { type: 'section', title: "1. La démarche qualité dans le secteur social et médico-social",
    narration: "La démarche qualité dans le secteur social et médico-social." },
  // SLIDE 55
  { type: 'content', title: "La démarche qualité dans le secteur social",
    intro: "La démarche qualité peut être définie comme : un processus structuré visant à améliorer de manière continue la qualité des services rendus aux usagers.",
    intro2: "Dans le secteur social et médico-social, la qualité ne se limite pas à l'efficacité des interventions. Elle concerne également :",
    points: [
    "Le respect des droits des usagers",
    "La pertinence de l'accompagnement",
    "La participation des personnes accompagnées",
    "La coordination des interventions",
    "L'amélioration des pratiques professionnelles"
  ], highlight: "La démarche qualité vise donc à garantir que les actions menées par les professionnels répondent aux besoins des publics tout en respectant les exigences réglementaires et éthiques.",
    narration: "La démarche qualité est un processus structuré visant à améliorer en continu la qualité des services rendus aux usagers. Elle concerne le respect des droits, la pertinence de l'accompagnement, la participation, la coordination et l'amélioration des pratiques." },
  // SLIDE 57 — Titre section 2
  { type: 'section', title: "2. Les principes de l'amélioration continue",
    narration: "Les principes de l'amélioration continue." },
  // SLIDE 58 — L'amélioration continue
  { type: 'content', title: "L'amélioration continue",
    intro: "La démarche qualité repose sur plusieurs principes essentiels.",
    intro2: "L'amélioration continue consiste à analyser régulièrement les pratiques afin d'identifier les axes de progrès.",
    intro3: "Elle suppose :",
    points: [
      "L'évaluation des actions",
      "L'analyse des résultats",
      "La mise en place d'actions correctives"
    ],
    narration: "L'amélioration continue consiste à analyser régulièrement les pratiques afin d'identifier les axes de progrès. Elle suppose l'évaluation des actions, l'analyse des résultats et la mise en place d'actions correctives." },
  // SLIDE 59 — La participation des professionnels
  { type: 'content', title: "La participation des professionnels",
    intro: "La qualité ne peut être garantie sans l'implication des équipes.",
    intro2: "Les professionnels doivent être associés :",
    points: [
      "À l'analyse des pratiques",
      "À la réflexion sur l'organisation du service",
      "À la mise en œuvre des actions d'amélioration"
    ],
    narration: "La qualité ne peut être garantie sans l'implication des équipes. Les professionnels doivent être associés à l'analyse des pratiques, à la réflexion sur l'organisation et à la mise en œuvre des actions d'amélioration." },
  // SLIDE 60 — La prise en compte des usagers
  { type: 'content', title: "La prise en compte des usagers",
    intro: "Les usagers sont des acteurs de leur accompagnement.",
    highlight: "Leur participation constitue un élément central de la démarche qualité.",
    narration: "Les usagers sont des acteurs de leur accompagnement. Leur participation constitue un élément central de la démarche qualité." },
  // SLIDE 61 — La roue de Deming (PDCA)
  { type: 'method', title: "L'amélioration continue : la roue de Deming", steps: [
    { label: "Plan (Planifier)", desc: "Identifier un problème ou un objectif d'amélioration et définir les actions à mettre en place" },
    { label: "Do (Mettre en œuvre)", desc: "Réaliser les actions prévues" },
    { label: "Check (Évaluer)", desc: "Analyser les résultats obtenus" },
    { label: "Act (Ajuster)", desc: "Mettre en place des actions correctives ou améliorer le dispositif" }
  ], quote: "Ce cycle permet de faire évoluer progressivement les pratiques professionnelles.",
    narration: "La roue de Deming comporte 4 étapes : Plan pour planifier, Do pour mettre en œuvre, Check pour évaluer, Act pour ajuster. Ce cycle permet de faire évoluer progressivement les pratiques professionnelles." },
  // SLIDE 62 — Titre section 3
  { type: 'section', title: "3. La culture de la bientraitance",
    narration: "La culture de la bientraitance." },
  // SLIDE 63 — La culture de la bientraitance
  { type: 'content', title: "La culture de la bientraitance",
    intro: "La bientraitance constitue un principe fondamental dans l'accompagnement des publics.",
    intro2: "Elle peut être définie comme :",
    highlight: "Une démarche collective visant à promouvoir le respect, la dignité et le bien-être des personnes accompagnées.",
    intro3: "La bientraitance implique :",
    points: [
      "Une attention constante aux besoins des usagers",
      "Une réflexion éthique sur les pratiques professionnelles",
      "La prévention des situations de maltraitance"
    ],
    quote: "Le responsable d'unité joue un rôle important dans la promotion de cette culture au sein de l'équipe.",
    narration: "La bientraitance constitue un principe fondamental. Elle implique une attention constante aux besoins des usagers, une réflexion éthique et la prévention de la maltraitance. Le responsable d'unité joue un rôle important dans la promotion de cette culture." },
  // SLIDE 64 — Titre section 4
  { type: 'section', title: "4. La méthodologie des projets personnalisés",
    narration: "La méthodologie des projets personnalisés." },
  // SLIDE 65 — Définition du projet personnalisé
  { type: 'content', title: "Définition",
    intro: "Le projet personnalisé est un outil central de l'accompagnement dans le secteur social et médico-social.",
    intro2: "Il correspond à :",
    highlight: "Un document formalisé qui définit les objectifs et les modalités d'accompagnement d'une personne.",
    intro3: "Le projet personnalisé vise à :",
    points: [
      "Individualiser l'accompagnement",
      "Prendre en compte les besoins et les attentes de la personne",
      "Coordonner les interventions des professionnels"
    ],
    narration: "Le projet personnalisé est un outil central de l'accompagnement. Il correspond à un document formalisé qui définit les objectifs et les modalités d'accompagnement d'une personne. Il vise à individualiser l'accompagnement, prendre en compte les besoins et coordonner les interventions." },
  // SLIDE 66 — Méthodologie (conservée)
  { type: 'method', title: "Méthodologie d'élaboration du projet personnalisé", steps: [
    { label: "Évaluation de la situation", desc: "Analyser les besoins, ressources, difficultés et environnement social et familial de la personne" },
    { label: "Définition des objectifs", desc: "Objectifs réalistes, adaptés aux besoins, évaluables (autonomie, insertion sociale, communication…)" },
    { label: "Mise en œuvre des actions", desc: "Accompagnement éducatif, suivi thérapeutique, activités de socialisation" },
    { label: "Évaluation du projet", desc: "Vérifier la pertinence des objectifs, l'efficacité des actions, l'évolution de la situation" }
  ], narration: "La méthodologie comprend 4 étapes : évaluation de la situation, définition des objectifs, mise en œuvre des actions et évaluation du projet." },
  // SLIDE 67 — L'évaluation de la situation
  { type: 'content', title: "L'évaluation de la situation",
    intro: "Cette étape consiste à analyser :",
    points: [
      "Les besoins de la personne",
      "Ses ressources",
      "Ses difficultés",
      "Son environnement social et familial"
    ],
    narration: "L'évaluation de la situation consiste à analyser les besoins de la personne, ses ressources, ses difficultés et son environnement social et familial." },
  // SLIDE 68 — La définition des objectifs
  { type: 'content', title: "La définition des objectifs",
    intro: "Les objectifs doivent être :",
    points: [
      "Réalistes",
      "Adaptés aux besoins de la personne",
      "Évaluables"
    ],
    intro2: "Par exemple :",
    points2: [
      "Développer l'autonomie dans les actes de la vie quotidienne",
      "Favoriser l'insertion sociale",
      "Améliorer la communication"
    ],
    narration: "Les objectifs doivent être réalistes, adaptés aux besoins et évaluables. Par exemple : développer l'autonomie, favoriser l'insertion sociale, améliorer la communication." },
  // SLIDE 69 — La mise en œuvre des actions
  { type: 'content', title: "La mise en œuvre des actions",
    intro: "Les professionnels définissent les actions nécessaires pour atteindre les objectifs.",
    intro2: "Ces actions peuvent concerner :",
    points: [
      "Un accompagnement éducatif",
      "Un suivi thérapeutique",
      "Des activités de socialisation"
    ],
    narration: "Les professionnels définissent les actions nécessaires. Ces actions peuvent concerner un accompagnement éducatif, un suivi thérapeutique ou des activités de socialisation." },
  // SLIDE 70 — L'évaluation du projet
  { type: 'content', title: "L'évaluation du projet",
    intro: "Le projet personnalisé doit être évalué régulièrement afin de vérifier :",
    points: [
      "La pertinence des objectifs",
      "L'efficacité des actions mises en place",
      "L'évolution de la situation de la personne"
    ],
    narration: "Le projet personnalisé doit être évalué régulièrement afin de vérifier la pertinence des objectifs, l'efficacité des actions et l'évolution de la situation de la personne." },
  // ===== SECTION 5 — La co-construction avec l'usager =====
  // SLIDE 71 — Titre section 5
  { type: 'section', title: "5. La co-construction avec l'usager",
    narration: "La co-construction avec l'usager." },
  // SLIDE 72 — La contractualisation avec l'usager
  { type: 'content', title: "La contractualisation avec l'usager",
    intro: "Le projet personnalisé doit être construit avec la personne accompagnée.",
    intro2: "Cette co-construction permet :",
    points: [
      "De respecter l'autonomie de l'usager",
      "De favoriser son implication",
      "D'adapter les interventions à ses attentes"
    ],
    intro3: "La contractualisation peut prendre la forme :",
    points2: [
      "D'un document signé",
      "D'un engagement mutuel entre l'usager et l'institution"
    ],
    narration: "Le projet personnalisé doit être co-construit avec la personne accompagnée. Cette co-construction permet de respecter l'autonomie, favoriser l'implication et adapter les interventions. La contractualisation peut prendre la forme d'un document signé ou d'un engagement mutuel." },

  // ===== SECTION 6 — L'évaluation des projets personnalisés =====
  // SLIDE 73 — Titre section 6
  { type: 'section', title: "6. L'évaluation des projets personnalisés",
    narration: "L'évaluation des projets personnalisés." },
  // SLIDE 74 — Les référentiels qualité — La HAS
  { type: 'content', title: "La Haute Autorité de Santé (HAS)",
    intro: "La Haute Autorité de Santé est chargée de définir les orientations en matière de qualité dans les établissements sociaux et médico-sociaux.",
    intro2: "Elle élabore notamment :",
    points: [
      "Des référentiels d'évaluation",
      "Des recommandations professionnelles",
      "Des outils d'amélioration des pratiques"
    ],
    narration: "La HAS est chargée de définir les orientations qualité. Elle élabore des référentiels d'évaluation, des recommandations professionnelles et des outils d'amélioration des pratiques." },
  // SLIDE 75 — Les recommandations de bonnes pratiques professionnelles
  { type: 'content', title: "Les recommandations de bonnes pratiques professionnelles",
    intro: "Les recommandations de bonnes pratiques professionnelles (RBPP) constituent des repères pour les professionnels.",
    intro2: "Elles visent à :",
    points: [
      "Améliorer la qualité de l'accompagnement",
      "Harmoniser les pratiques",
      "Diffuser les connaissances professionnelles"
    ],
    narration: "Les RBPP constituent des repères pour les professionnels. Elles visent à améliorer la qualité de l'accompagnement, harmoniser les pratiques et diffuser les connaissances professionnelles." },
  // SLIDE 76 — Le Conseil de la Vie Sociale (CVS)
  { type: 'content', title: "Le Conseil de la Vie Sociale (CVS)",
    intro: "Le Conseil de la Vie Sociale est une instance permettant aux usagers et à leurs familles de participer au fonctionnement de l'établissement.",
    intro2: "Il permet notamment :",
    points: [
      "D'exprimer les attentes des usagers",
      "De recueillir leur avis sur l'organisation du service",
      "D'améliorer la qualité de l'accompagnement"
    ],
    narration: "Le CVS permet aux usagers et à leurs familles de participer au fonctionnement de l'établissement. Il permet d'exprimer les attentes, de recueillir les avis et d'améliorer la qualité de l'accompagnement." },
  // SLIDE 77 — Les enquêtes de satisfaction
  { type: 'content', title: "Les enquêtes de satisfaction",
    intro: "Les enquêtes de satisfaction permettent de recueillir l'avis des usagers sur les services proposés.",
    intro2: "Elles constituent un outil précieux pour :",
    points: [
      "Identifier les points forts",
      "Repérer les axes d'amélioration",
      "Ajuster l'organisation du service"
    ],
    narration: "Les enquêtes de satisfaction permettent de recueillir l'avis des usagers. Elles constituent un outil précieux pour identifier les points forts, repérer les axes d'amélioration et ajuster l'organisation du service." },

  // ===== SECTION 7 — Les projets collectifs et la dynamique de groupe =====
  // SLIDE 78 — Titre section 7
  { type: 'section', title: "7. Les projets collectifs et la dynamique de groupe",
    narration: "Les projets collectifs et la dynamique de groupe." },
  // SLIDE 79 — Les projets collectifs — Définition
  { type: 'content', title: "Les projets collectifs — Définition",
    intro: "Les projets collectifs visent à organiser des actions impliquant plusieurs usagers.",
    intro2: "Ils permettent notamment de :",
    points: [
      "Développer les interactions sociales",
      "Favoriser la participation",
      "Soutenir l'autonomie"
    ],
    narration: "Les projets collectifs visent à organiser des actions impliquant plusieurs usagers. Ils permettent de développer les interactions sociales, favoriser la participation et soutenir l'autonomie." },
  // SLIDE 80 — La dynamique de groupe
  { type: 'content', title: "La dynamique de groupe",
    intro: "La dynamique de groupe correspond à l'ensemble des interactions entre les membres d'un groupe.",
    intro2: "Dans un projet collectif, il est important de prendre en compte :",
    points: [
      "Les relations entre les participants",
      "Les rôles de chacun",
      "Les modes de communication"
    ],
    highlight: "Le professionnel doit être capable d'animer le groupe tout en favorisant l'expression de chacun.",
    narration: "La dynamique de groupe correspond à l'ensemble des interactions entre les membres d'un groupe. Il est important de prendre en compte les relations, les rôles et les modes de communication. Le professionnel doit animer le groupe tout en favorisant l'expression de chacun." },
  // SLIDE 81 — Les ateliers éducatifs et thérapeutiques
  { type: 'content', title: "Les ateliers éducatifs et thérapeutiques",
    intro: "Les projets collectifs peuvent prendre différentes formes :",
    points: [
      "Ateliers éducatifs",
      "Activités culturelles",
      "Groupes de parole",
      "Activités sportives",
      "Ateliers thérapeutiques"
    ],
    intro2: "Ces activités permettent de développer :",
    points2: [
      "Les compétences sociales",
      "L'autonomie",
      "L'estime de soi"
    ],
    narration: "Les projets collectifs peuvent prendre différentes formes : ateliers éducatifs, activités culturelles, groupes de parole, activités sportives, ateliers thérapeutiques. Ces activités développent les compétences sociales, l'autonomie et l'estime de soi." },
  // SLIDE 82 — L'animation de la vie sociale
  { type: 'content', title: "L'animation de la vie sociale",
    intro: "Dans certains établissements, l'animation de la vie sociale constitue un axe important de l'accompagnement.",
    intro2: "Elle vise à :",
    points: [
      "Favoriser les échanges entre usagers",
      "Créer des espaces de participation",
      "Renforcer le sentiment d'appartenance à un collectif"
    ],
    narration: "L'animation de la vie sociale vise à favoriser les échanges entre usagers, créer des espaces de participation et renforcer le sentiment d'appartenance à un collectif." },

  // ===== CONCLUSION DU CHAPITRE 2 =====
  // SLIDE 83 — Conclusion
  { type: 'content', title: "Conclusion",
    intro: "La qualité des projets individuels et collectifs constitue un élément central du pilotage d'une unité d'intervention sociale.",
    intro2: "Le responsable d'unité doit veiller à :",
    points: [
      "Structurer la démarche qualité",
      "Garantir la pertinence des projets personnalisés",
      "Soutenir la mise en œuvre de projets collectifs",
      "Favoriser la participation des usagers",
      "Intégrer les recommandations professionnelles"
    ],
    highlight: "Ces actions contribuent à améliorer la qualité de l'accompagnement et à renforcer la cohérence des pratiques professionnelles.",
    narration: "La qualité des projets individuels et collectifs est centrale dans le pilotage d'une unité. Le responsable d'unité doit structurer la démarche qualité, garantir la pertinence des projets, soutenir les projets collectifs, favoriser la participation des usagers et intégrer les recommandations professionnelles." },
  // SLIDE 84 — À retenir
  { type: 'jurybox', title: "À retenir — Chapitre 2", points: [
    "La démarche qualité est un processus continu (cycle PDCA de Deming)",
    "La bientraitance est un principe fondamental de l'accompagnement",
    "Le projet personnalisé est co-construit avec l'usager (4 étapes)",
    "Les projets collectifs développent la participation et la vie sociale",
    "Les référentiels HAS et RBPP guident les pratiques professionnelles",
    "Le CVS et les enquêtes de satisfaction favorisent la participation des usagers"
  ], narration: "Les points clés du Chapitre 2 : démarche qualité et PDCA, bientraitance, projet personnalisé co-construit, projets collectifs, référentiels HAS et RBPP, participation des usagers via le CVS et les enquêtes de satisfaction." },

  // ============================================
  // CHAPITRE 3 — ÉVALUER LES RÉSULTATS
  // ============================================

  // SLIDE 85 — Transition Chapitre 3
  { type: 'section', title: "Chapitre 3", subtitle: "Évaluer les résultats de la mise en œuvre de l'activité",
    narration: "Chapitre 3 : Évaluer les résultats de la mise en œuvre de l'activité." },

  // SLIDE 86 — Introduction Chapitre 3
  { type: 'content', title: "Introduction",
    intro: "Dans le secteur social et médico-social, l'évaluation constitue un outil essentiel pour garantir la qualité et la pertinence des actions mises en œuvre auprès des usagers.",
    intro2: "Le responsable d'unité ne se limite pas à organiser et coordonner l'activité. Il doit également être en mesure d'analyser les résultats obtenus afin de vérifier si les objectifs fixés ont été atteints.",
    intro3: "L'évaluation permet notamment de :",
    points: [
      "Mesurer l'efficacité des actions mises en place",
      "Identifier les axes d'amélioration",
      "Ajuster l'organisation du service",
      "Rendre compte de l'activité à la direction et aux partenaires"
    ],
    highlight: "Elle s'inscrit dans une logique d'amélioration continue des pratiques professionnelles et de pilotage stratégique de l'activité.",
    narration: "L'évaluation est un outil essentiel pour garantir la qualité des actions. Elle permet de mesurer l'efficacité, identifier les axes d'amélioration, ajuster l'organisation et rendre compte de l'activité." },

  // SLIDE 87 — Objectifs pédagogiques du Chapitre 3
  { type: 'content', title: "Objectifs pédagogiques du Chapitre 3",
    intro: "À l'issue de ce module, le professionnel doit être capable de :",
    points: [
      "Comprendre les principes de l'évaluation dans le secteur social et médico-social",
      "Distinguer les notions de contrôle et d'évaluation",
      "Construire des indicateurs pertinents pour mesurer l'activité",
      "Utiliser des tableaux de bord pour piloter une unité",
      "Analyser les écarts entre objectifs et résultats",
      "Élaborer des plans d'actions correctives",
      "Communiquer les résultats à l'équipe et à la direction"
    ],
    narration: "Les objectifs du Chapitre 3 couvrent les principes d'évaluation, la distinction contrôle-évaluation, les indicateurs, les tableaux de bord, l'analyse des écarts, les plans d'actions et la communication des résultats." },

  // SLIDE 88 — Sommaire du Chapitre 3
  { type: 'sommaire', title: "Sommaire du Chapitre 3",
    sections: [
      { num: "1", title: "Les principes de l'évaluation dans le secteur social", targetSlide: 89 },
      { num: "2", title: "Différence entre contrôle et évaluation", targetSlide: 91 },
      { num: "3", title: "Les critères d'évaluation", targetSlide: 94 },
      { num: "4", title: "Les indicateurs de suivi de l'activité", targetSlide: 97 },
      { num: "5", title: "Les tableaux de bord de pilotage", targetSlide: 102 },
      { num: "6", title: "L'analyse des écarts entre objectifs et résultats", targetSlide: 105 },
      { num: "7", title: "L'identification des causes des dysfonctionnements", targetSlide: 108 },
      { num: "8", title: "Les plans d'actions correctives", targetSlide: 111 },
      { num: "9", title: "Le reporting et la communication des résultats", targetSlide: 114 }
    ],
    narration: "Le Chapitre 3 couvre 9 thèmes : les principes de l'évaluation, contrôle et évaluation, les critères, les indicateurs, les tableaux de bord, l'analyse des écarts, les causes de dysfonctionnements, les plans d'actions correctives et le reporting." },

  // ===== SECTION 1 — Les principes de l'évaluation =====
  // SLIDE 89
  { type: 'section', title: "1. Les principes de l'évaluation dans le secteur social",
    narration: "Les principes de l'évaluation dans le secteur social." },
  // SLIDE 90 — Définition de l'évaluation
  { type: 'content', title: "Définition de l'évaluation",
    intro: "L'évaluation peut être définie comme :",
    highlight: "Un processus d'analyse visant à apprécier les résultats d'une action au regard des objectifs fixés.",
    intro2: "Elle permet de déterminer :",
    points: [
      "Si les actions mises en œuvre sont efficaces",
      "Si elles répondent aux besoins des usagers",
      "Si les ressources mobilisées sont adaptées"
    ],
    quote: "Dans le secteur social et médico-social, l'évaluation contribue à garantir la qualité de l'accompagnement et la pertinence des interventions.",
    narration: "L'évaluation est un processus d'analyse visant à apprécier les résultats d'une action au regard des objectifs fixés. Elle contribue à garantir la qualité de l'accompagnement." },

  // ===== SECTION 2 — Différence entre contrôle et évaluation =====
  // SLIDE 91
  { type: 'section', title: "2. Différence entre contrôle et évaluation",
    narration: "Différence entre contrôle et évaluation." },
  // SLIDE 92 — Le contrôle
  { type: 'content', title: "Le contrôle",
    intro: "Le contrôle consiste à vérifier la conformité d'une action par rapport à une norme ou à une règle.",
    intro2: "Exemples :",
    points: [
      "Vérifier que les procédures sont respectées",
      "Contrôler la présence des documents obligatoires",
      "Vérifier l'application des protocoles"
    ],
    highlight: "Le contrôle repose principalement sur une logique de conformité.",
    narration: "Le contrôle consiste à vérifier la conformité d'une action par rapport à une norme. Il repose sur une logique de conformité." },
  // SLIDE 93 — L'évaluation
  { type: 'content', title: "L'évaluation",
    intro: "L'évaluation vise à analyser les résultats et l'impact des actions menées.",
    intro2: "Elle cherche à répondre à plusieurs questions :",
    points: [
      "Les objectifs ont-ils été atteints ?",
      "Les actions mises en place sont-elles pertinentes ?",
      "Quels effets ont-elles sur les usagers ?"
    ],
    highlight: "L'évaluation repose donc sur une logique d'amélioration et d'apprentissage.",
    narration: "L'évaluation vise à analyser les résultats et l'impact des actions. Elle repose sur une logique d'amélioration et d'apprentissage." },

  // ===== SECTION 3 — Les critères d'évaluation =====
  // SLIDE 94
  { type: 'section', title: "3. Les critères d'évaluation",
    narration: "Les critères d'évaluation." },
  // SLIDE 95 — Les critères d'évaluation
  { type: 'content', title: "Les critères d'évaluation",
    intro: "Les critères d'évaluation correspondent aux éléments permettant d'apprécier la qualité ou l'efficacité d'une action.",
    intro2: "Ils peuvent porter sur :",
    points: [
      "La pertinence",
      "L'efficacité",
      "L'efficience",
      "La cohérence",
      "L'impact"
    ],
    narration: "Les critères d'évaluation sont la pertinence, l'efficacité, l'efficience, la cohérence et l'impact." },
  // SLIDE 96 — Définitions des critères
  { type: 'table', title: "Définitions des critères d'évaluation", rows: [
    { situation: "Pertinence", besoin: "Adéquation entre les actions menées et les besoins des usagers" },
    { situation: "Efficacité", besoin: "Degré d'atteinte des objectifs fixés" },
    { situation: "Efficience", besoin: "Rapport entre les résultats obtenus et les moyens mobilisés" },
    { situation: "Cohérence", besoin: "Alignement des actions avec les orientations institutionnelles" },
    { situation: "Impact", besoin: "Effets durables des actions sur les usagers et l'organisation" }
  ], narration: "La pertinence est l'adéquation entre actions et besoins. L'efficacité mesure l'atteinte des objectifs. L'efficience compare résultats et moyens." },

  // ===== SECTION 4 — Les indicateurs de suivi de l'activité =====
  // SLIDE 97
  { type: 'section', title: "4. Les indicateurs de suivi de l'activité",
    narration: "Les indicateurs de suivi de l'activité." },
  // SLIDE 98 — Définition des indicateurs
  { type: 'content', title: "Les indicateurs — Définition",
    intro: "Un indicateur est un outil permettant de mesurer un phénomène ou d'apprécier l'évolution d'une situation.",
    highlight: "Les indicateurs permettent de suivre l'activité d'un service et d'évaluer les résultats obtenus.",
    narration: "Un indicateur est un outil permettant de mesurer un phénomène. Les indicateurs permettent de suivre l'activité et d'évaluer les résultats." },
  // SLIDE 99 — Indicateurs quantitatifs
  { type: 'content', title: "Les indicateurs quantitatifs",
    intro: "Les indicateurs quantitatifs mesurent des données chiffrées.",
    intro2: "Exemples :",
    points: [
      "Nombre d'usagers accompagnés",
      "Taux d'occupation d'un établissement",
      "Nombre d'actions éducatives réalisées"
    ],
    narration: "Les indicateurs quantitatifs mesurent des données chiffrées comme le nombre d'usagers accompagnés ou le taux d'occupation." },
  // SLIDE 100 — Indicateurs qualitatifs
  { type: 'content', title: "Les indicateurs qualitatifs",
    intro: "Les indicateurs qualitatifs permettent d'apprécier la qualité des interventions.",
    intro2: "Exemples :",
    points: [
      "Satisfaction des usagers",
      "Qualité des projets personnalisés",
      "Évolution de l'autonomie des personnes accompagnées"
    ],
    narration: "Les indicateurs qualitatifs permettent d'apprécier la qualité des interventions, comme la satisfaction des usagers ou l'évolution de l'autonomie." },

  // ===== SECTION 5 — Les tableaux de bord de pilotage =====
  // SLIDE 102
  { type: 'section', title: "5. Les tableaux de bord de pilotage",
    narration: "Les tableaux de bord de pilotage." },
  // SLIDE 103 — Définition du tableau de bord
  { type: 'content', title: "Le tableau de bord — Définition",
    intro: "Le tableau de bord est un outil de pilotage permettant de suivre l'activité d'un service à partir d'indicateurs.",
    intro2: "Il permet de :",
    points: [
      "Visualiser les résultats",
      "Identifier les tendances",
      "Détecter les difficultés",
      "Faciliter la prise de décision"
    ],
    narration: "Le tableau de bord est un outil de pilotage qui permet de visualiser les résultats, identifier les tendances, détecter les difficultés et faciliter la prise de décision." },
  // SLIDE 104 — Les fonctions du tableau de bord
  { type: 'content', title: "Les fonctions du tableau de bord",
    intro: "Un tableau de bord permet notamment de :",
    points: [
      "Suivre l'évolution de l'activité",
      "Mesurer l'atteinte des objectifs",
      "Anticiper les difficultés",
      "Soutenir la prise de décision"
    ],
    narration: "Le tableau de bord permet de suivre l'évolution de l'activité, mesurer l'atteinte des objectifs, anticiper les difficultés et soutenir la prise de décision." },
  // SLIDE — Tableau des indicateurs (EMBELLI)
  { type: 'indicator_table', title: "Exemples d'indicateurs dans une unité sociale",
    headers: ["Domaine", "Indicateur"],
    data: [
      { domaine: "Activité", indicateur: "Nombre d'usagers accompagnés" },
      { domaine: "Qualité", indicateur: "Taux de projets personnalisés actualisés" },
      { domaine: "Organisation", indicateur: "Taux de réunions d'équipe réalisées" },
      { domaine: "Satisfaction", indicateur: "Résultats des enquêtes auprès des usagers" }
    ],
    narration: "Les indicateurs couvrent plusieurs domaines : activité, qualité, organisation et satisfaction." },

  // ===== SECTION 6 — L'analyse des écarts =====
  // SLIDE 105
  { type: 'section', title: "6. L'analyse des écarts entre objectifs et résultats",
    narration: "L'analyse des écarts entre objectifs et résultats." },
  // SLIDE 106 — Définition
  { type: 'content', title: "L'analyse des écarts — Définition",
    intro: "L'analyse des écarts consiste à comparer :",
    points: [
      "Les résultats obtenus",
      "Les objectifs fixés"
    ],
    highlight: "Lorsque les résultats ne correspondent pas aux attentes, il est nécessaire d'identifier les causes de ces écarts.",
    narration: "L'analyse des écarts consiste à comparer les résultats obtenus aux objectifs fixés. Il est nécessaire d'identifier les causes de ces écarts." },
  // SLIDE 107 — Les causes possibles des écarts
  { type: 'content', title: "Les causes possibles des écarts",
    intro: "Les écarts peuvent être liés à différents facteurs :",
    points: [
      "Organisationnels",
      "Humains",
      "Matériels",
      "Contextuels"
    ],
    intro2: "Par exemple :",
    points2: [
      "Manque de ressources humaines",
      "Mauvaise communication",
      "Objectifs irréalistes",
      "Évolution des besoins des usagers"
    ],
    narration: "Les écarts peuvent être liés à des facteurs organisationnels, humains, matériels ou contextuels." },

  // ===== SECTION 7 — L'identification des causes des dysfonctionnements =====
  // SLIDE 108
  { type: 'section', title: "7. L'identification des causes des dysfonctionnements",
    narration: "L'identification des causes des dysfonctionnements." },
  // SLIDE 109 — La méthode des 5 pourquoi
  { type: 'content', title: "La méthode des « 5 pourquoi »",
    intro: "Pour comprendre les causes d'un problème, il est possible d'utiliser certaines méthodes d'analyse.",
    highlight: "La méthode des « 5 pourquoi » consiste à poser plusieurs fois la question « pourquoi ? » afin d'identifier l'origine du problème.",
    narration: "La méthode des 5 pourquoi consiste à poser plusieurs fois la question pourquoi afin d'identifier l'origine du problème." },
  // SLIDE 110 — Le diagramme d'Ishikawa
  { type: 'content', title: "Le diagramme d'Ishikawa",
    intro: "Ce diagramme permet de représenter les différentes causes possibles d'un dysfonctionnement.",
    intro2: "Les causes peuvent être regroupées en plusieurs catégories :",
    points: [
      "Méthodes",
      "Moyens",
      "Milieu",
      "Matériel",
      "Main-d'œuvre"
    ],
    narration: "Le diagramme d'Ishikawa permet de représenter les différentes causes possibles d'un dysfonctionnement, regroupées en 5 catégories : méthodes, moyens, milieu, matériel et main-d'œuvre." },

  // ===== SECTION 8 — Les plans d'actions correctives =====
  // SLIDE 111
  { type: 'section', title: "8. Les plans d'actions correctives",
    narration: "Les plans d'actions correctives." },
  // SLIDE 112 — Les plans d'actions correctives
  { type: 'content', title: "Les plans d'actions correctives",
    intro: "Une fois les causes identifiées, il est nécessaire de définir des actions permettant d'améliorer la situation.",
    intro2: "Un plan d'action doit préciser :",
    points: [
      "Les actions à réaliser",
      "Les responsables",
      "Les délais",
      "Les indicateurs de suivi"
    ],
    narration: "Un plan d'action doit préciser les actions à réaliser, les responsables, les délais et les indicateurs de suivi." },
  // SLIDE 113 — Tableau plan d'action (EMBELLI)
  { type: 'action_plan_table', title: "Exemple de plan d'action correctif",
    headers: ["Problème identifié", "Action", "Responsable", "Délai"],
    data: [
      ["Retard dans les projets personnalisés", "Mise en place d'un calendrier de suivi", "Responsable d'unité", "3 mois"]
    ],
    quote: "Un plan d'action structuré permet de suivre la mise en œuvre des améliorations et de vérifier leur efficacité.",
    narration: "Exemple : le problème de retard dans les projets personnalisés est résolu par la mise en place d'un calendrier de suivi, sous la responsabilité du responsable d'unité, dans un délai de 3 mois." },

  // ===== SECTION 9 — Le reporting et la communication des résultats =====
  // SLIDE 114
  { type: 'section', title: "9. Le reporting et la communication des résultats",
    narration: "Le reporting et la communication des résultats." },
  // SLIDE 115 — Définition du reporting
  { type: 'content', title: "Le reporting — Définition",
    intro: "Le reporting correspond à l'ensemble des informations transmises à la direction ou aux partenaires afin de rendre compte de l'activité du service.",
    highlight: "Il constitue un outil de communication et de pilotage.",
    narration: "Le reporting correspond à l'ensemble des informations transmises pour rendre compte de l'activité. Il constitue un outil de communication et de pilotage." },
  // SLIDE 116 — Les objectifs du reporting
  { type: 'content', title: "Les objectifs du reporting",
    intro: "Le reporting permet :",
    points: [
      "De présenter les résultats obtenus",
      "D'expliquer les écarts éventuels",
      "De valoriser les actions réalisées",
      "De proposer des axes d'amélioration"
    ],
    narration: "Le reporting permet de présenter les résultats, expliquer les écarts, valoriser les actions et proposer des axes d'amélioration." },
  // SLIDE 117 — La rédaction d'un rapport d'activité
  { type: 'content', title: "La rédaction d'un rapport d'activité",
    intro: "Le rapport d'activité constitue un document de synthèse présentant l'ensemble des actions menées par un service sur une période donnée.",
    intro2: "Il peut comprendre :",
    points: [
      "Une présentation du service",
      "Les objectifs poursuivis",
      "Les actions réalisées",
      "Les résultats obtenus",
      "Les perspectives d'évolution"
    ],
    narration: "Le rapport d'activité présente le service, les objectifs poursuivis, les actions réalisées, les résultats obtenus et les perspectives d'évolution." },
  // SLIDE 118 — La communication des résultats
  { type: 'content', title: "La communication des résultats",
    intro: "La communication des résultats constitue une étape essentielle du processus d'évaluation.",
    intro2: "Elle permet :",
    points: [
      "De partager les informations avec l'équipe",
      "De valoriser le travail des professionnels",
      "De favoriser la participation à l'amélioration des pratiques"
    ],
    intro3: "Le responsable d'unité peut utiliser différents supports :",
    points2: [
      "Réunions d'équipe",
      "Synthèses écrites",
      "Présentations visuelles",
      "Rapports d'activité"
    ],
    narration: "La communication des résultats permet de partager les informations, valoriser le travail et favoriser l'amélioration des pratiques. Le responsable d'unité peut utiliser des réunions, synthèses écrites, présentations visuelles et rapports d'activité." },

  // ===== CONCLUSION DU CHAPITRE 3 =====
  // SLIDE 119 — Conclusion
  { type: 'content', title: "Conclusion du Chapitre 3",
    intro: "L'évaluation des résultats constitue un levier essentiel pour piloter l'activité d'une unité d'intervention sociale.",
    intro2: "Elle permet :",
    points: [
      "D'analyser l'efficacité des actions",
      "D'améliorer l'organisation du service",
      "De renforcer la qualité de l'accompagnement"
    ],
    highlight: "Le responsable d'unité doit donc développer des compétences en matière d'analyse, de pilotage et de communication afin de soutenir l'amélioration continue des pratiques professionnelles.",
    narration: "L'évaluation des résultats est un levier essentiel pour piloter l'activité. Le responsable d'unité doit développer des compétences en analyse, pilotage et communication." },
  // SLIDE 120 — À retenir
  { type: 'jurybox', title: "À retenir — Chapitre 3", points: [
    "Distinguer contrôle (conformité) et évaluation (amélioration)",
    "Construire des indicateurs quantitatifs et qualitatifs",
    "Les tableaux de bord sont des outils de pilotage essentiels",
    "Analyser les écarts avec les « 5 pourquoi » et le diagramme d'Ishikawa",
    "Élaborer des plans d'actions correctives avec responsables et délais",
    "Communiquer les résultats via le reporting et les rapports d'activité"
  ], narration: "Les points clés du Chapitre 3 : contrôle vs évaluation, indicateurs, tableaux de bord, analyse des écarts, plans d'actions et reporting." },

  // ============================================
  // CHAPITRE 4 — METTRE EN PLACE UN PARTENARIAT
  // ============================================

  // SLIDE 121 — Transition Chapitre 4
  { type: 'section', title: "Chapitre 4", subtitle: "Mettre en place un partenariat au service de l'activité",
    narration: "Chapitre 4 : Mettre en place un partenariat au service de l'activité." },

  // SLIDE 122 — Introduction Chapitre 4
  { type: 'content', title: "Introduction",
    intro: "Les problématiques sociales et médico-sociales auxquelles sont confrontés les professionnels sont souvent complexes et multidimensionnelles. Les situations des usagers impliquent généralement plusieurs acteurs : services sociaux, établissements de santé, structures éducatives, services judiciaires ou encore collectivités territoriales.",
    intro2: "L'action d'une unité d'intervention sociale ne peut être envisagée de manière isolée. Elle s'inscrit dans un réseau de partenaires institutionnels et professionnels.",
    intro3: "Le partenariat constitue donc un levier essentiel pour :",
    points: [
      "Assurer la continuité des parcours des usagers",
      "Mobiliser des compétences complémentaires",
      "Coordonner les interventions",
      "Améliorer la qualité de l'accompagnement"
    ],
    highlight: "Le responsable d'unité joue un rôle central dans la structuration et l'animation de ces partenariats.",
    narration: "Le partenariat constitue un levier essentiel pour assurer la continuité des parcours, mobiliser des compétences complémentaires, coordonner les interventions et améliorer la qualité de l'accompagnement." },

  // SLIDE 123 — Objectifs pédagogiques du Chapitre 4
  { type: 'content', title: "Objectifs pédagogiques du Chapitre 4",
    intro: "À l'issue de ce module, le professionnel doit être capable de :",
    points: [
      "Identifier les partenaires institutionnels et associatifs d'un territoire",
      "Analyser les enjeux du travail en réseau",
      "Structurer des relations partenariales efficaces",
      "Formaliser les collaborations par des conventions ou protocoles",
      "Gérer les tensions ou conflits entre institutions"
    ],
    narration: "Les objectifs du Chapitre 4 : identifier les partenaires, structurer les relations, formaliser les collaborations et gérer les conflits." },

  // SLIDE 124 — Sommaire du Chapitre 4
  { type: 'sommaire', title: "Sommaire du Chapitre 4",
    sections: [
      { num: "1", title: "La notion de partenariat dans le secteur social", targetSlide: 125 },
      { num: "2", title: "Travail en réseau et coopération interinstitutionnelle", targetSlide: 128 },
      { num: "3", title: "La cartographie des partenaires du territoire", targetSlide: 132 },
      { num: "4", title: "Les acteurs du secteur sanitaire, social, éducatif et judiciaire", targetSlide: 135 },
      { num: "5", title: "Les étapes de construction d'un partenariat", targetSlide: 141 },
      { num: "6", title: "La formalisation des relations partenariales", targetSlide: 145 },
      { num: "7", title: "Le cadre juridique du partenariat (conventions, protocoles, CPOM)", targetSlide: 147 },
      { num: "8", title: "La gestion des tensions et conflits interinstitutionnels", targetSlide: 152 }
    ],
    narration: "Le Chapitre 4 couvre 8 thèmes : la notion de partenariat, le travail en réseau, la cartographie des partenaires, les acteurs du territoire, les étapes de construction, la formalisation, le cadre juridique et la gestion des conflits." },

  // ===== SECTION 1 — La notion de partenariat =====
  // SLIDE 125
  { type: 'section', title: "1. La notion de partenariat dans le secteur social",
    narration: "La notion de partenariat dans le secteur social." },
  // SLIDE 126 — Définition
  { type: 'content', title: "Définition du partenariat",
    intro: "Le partenariat peut être défini comme :",
    highlight: "Une relation de collaboration entre plusieurs acteurs visant à atteindre un objectif commun.",
    intro2: "Dans le secteur social et médico-social, le partenariat permet de mobiliser différentes compétences afin d'apporter une réponse globale aux besoins des usagers.",
    narration: "Le partenariat est une relation de collaboration entre plusieurs acteurs visant un objectif commun. Il permet de mobiliser différentes compétences pour répondre globalement aux besoins des usagers." },
  // SLIDE 127 — Les principes du partenariat
  { type: 'content', title: "Les principes du partenariat",
    intro: "Le partenariat repose sur plusieurs principes :",
    points: [
      "La reconnaissance des compétences de chacun",
      "La coopération entre institutions",
      "La recherche d'un objectif commun",
      "Le partage d'informations dans le respect du cadre légal"
    ],
    narration: "Le partenariat repose sur la reconnaissance des compétences, la coopération, la recherche d'un objectif commun et le partage d'informations." },

  // ===== SECTION 2 — Travail en réseau et coopération =====
  // SLIDE 128
  { type: 'section', title: "2. Travail en réseau et coopération interinstitutionnelle",
    narration: "Travail en réseau et coopération interinstitutionnelle." },
  // SLIDE 129 — Le travail en réseau
  { type: 'content', title: "Le travail en réseau",
    intro: "Le réseau correspond à l'ensemble des relations entre différents acteurs d'un territoire.",
    intro2: "Il peut s'agir :",
    points: [
      "De contacts informels",
      "D'échanges d'informations",
      "De collaborations ponctuelles"
    ],
    narration: "Le réseau correspond à l'ensemble des relations entre acteurs d'un territoire : contacts informels, échanges d'informations, collaborations ponctuelles." },
  // SLIDE 130 — Le partenariat structuré
  { type: 'content', title: "Le partenariat structuré",
    intro: "Le partenariat correspond à une relation plus structurée que le réseau.",
    intro2: "Il implique généralement :",
    points: [
      "Des objectifs partagés",
      "Des engagements réciproques",
      "Une formalisation des modalités de collaboration"
    ],
    highlight: "Le responsable d'unité doit être capable de transformer un réseau d'acteurs en véritable dynamique partenariale.",
    narration: "Le partenariat implique des objectifs partagés, des engagements réciproques et une formalisation. Le responsable d'unité doit transformer un réseau en dynamique partenariale." },
  // SLIDE 131 — Réseau vs Partenariat (comparaison visuelle)
  { type: 'comparison', title: "Réseau vs Partenariat", wrong: [
    { label: "Réseau : contacts informels", correct: "Partenariat : objectifs partagés" },
    { label: "Réseau : échanges ponctuels", correct: "Partenariat : engagements réciproques" },
    { label: "Réseau : collaborations non structurées", correct: "Partenariat : formalisation des modalités" },
    { label: "Réseau : relations individuelles", correct: "Partenariat : dynamique collective" }
  ], highlight: "Le partenariat va au-delà du réseau : il transforme des contacts en coopération structurée autour d'un projet commun.",
    narration: "Le réseau repose sur des contacts informels et ponctuels, tandis que le partenariat implique des objectifs partagés et une formalisation structurée." },

  // ===== SECTION 3 — La cartographie des partenaires =====
  // SLIDE 132
  { type: 'section', title: "3. La cartographie des partenaires du territoire",
    narration: "La cartographie des partenaires du territoire." },
  // SLIDE 133 — Définition
  { type: 'content', title: "La cartographie des partenaires",
    intro: "La cartographie des partenaires consiste à identifier et analyser les acteurs présents sur un territoire.",
    intro2: "Elle permet de comprendre :",
    points: [
      "Les ressources disponibles",
      "Les compétences mobilisables",
      "Les interactions entre les institutions"
    ],
    highlight: "Cette cartographie constitue un outil stratégique pour organiser la coopération.",
    narration: "La cartographie consiste à identifier et analyser les acteurs du territoire. Elle constitue un outil stratégique pour organiser la coopération." },
  // SLIDE 134 — Tableau cartographique (embelli)
  { type: 'indicator_table', title: "Cartographie territoriale — Exemple",
    headers: ["Domaine", "Partenaires"],
    data: [
      { domaine: "Santé", indicateur: "Hôpital, CMP, médecins libéraux" },
      { domaine: "Social", indicateur: "Services sociaux, associations, ESSMS" },
      { domaine: "Éducation", indicateur: "Écoles, collèges, missions locales" },
      { domaine: "Justice", indicateur: "PJJ, tribunaux, SPIP" },
      { domaine: "Collectivités", indicateur: "Conseil départemental, communes, CCAS" }
    ],
    narration: "La cartographie couvre 5 domaines : santé, social, éducation, justice et collectivités territoriales." },

  // ===== SECTION 4 — Les acteurs du territoire =====
  // SLIDE 135
  { type: 'section', title: "4. Les acteurs du secteur sanitaire, social, éducatif et judiciaire",
    narration: "Les acteurs du secteur sanitaire, social, éducatif et judiciaire." },
  // SLIDE 136 — Secteur sanitaire
  { type: 'content', title: "Le secteur sanitaire",
    intro: "Le secteur sanitaire regroupe les structures intervenant dans le domaine de la santé.",
    intro2: "Exemples :",
    points: [
      "Hôpitaux",
      "Centres médico-psychologiques",
      "Médecins libéraux",
      "Services de psychiatrie",
      "Services de soins à domicile"
    ],
    highlight: "Ces partenaires sont essentiels pour la prise en charge globale des usagers.",
    narration: "Le secteur sanitaire regroupe les hôpitaux, CMP, médecins libéraux, services de psychiatrie et de soins à domicile." },
  // SLIDE 137 — Secteur social et médico-social
  { type: 'content', title: "Le secteur social et médico-social",
    intro: "Il comprend :",
    points: [
      "Les établissements sociaux et médico-sociaux",
      "Les services d'aide à domicile",
      "Les services de protection de l'enfance",
      "Les structures d'insertion"
    ],
    highlight: "Ces institutions interviennent souvent auprès des mêmes publics. La coordination entre ces acteurs est donc essentielle.",
    narration: "Le secteur social comprend les ESSMS, les services d'aide à domicile, la protection de l'enfance et les structures d'insertion." },
  // SLIDE 138 — Secteur éducatif
  { type: 'content', title: "Le secteur éducatif",
    intro: "Les structures éducatives peuvent être des partenaires importants :",
    points: [
      "Établissements scolaires",
      "Services d'orientation",
      "Structures de formation"
    ],
    highlight: "Ces partenaires jouent un rôle central dans l'accompagnement des jeunes.",
    narration: "Le secteur éducatif comprend les établissements scolaires, les services d'orientation et les structures de formation." },
  // SLIDE 139 — Secteur judiciaire
  { type: 'content', title: "Le secteur judiciaire",
    intro: "Dans certaines situations, les services judiciaires peuvent intervenir.",
    intro2: "Exemples :",
    points: [
      "Protection judiciaire de la jeunesse",
      "Juges des enfants",
      "Services pénitentiaires d'insertion et de probation"
    ],
    highlight: "Ces institutions participent à la mise en œuvre de certaines mesures d'accompagnement.",
    narration: "Le secteur judiciaire comprend la PJJ, les juges des enfants et les services pénitentiaires d'insertion et de probation." },
  // SLIDE 140 — Collectivités territoriales
  { type: 'content', title: "Les collectivités territoriales",
    intro: "Les collectivités territoriales jouent un rôle majeur dans la mise en œuvre des politiques sociales.",
    intro2: "Il peut s'agir :",
    points: [
      "Des conseils départementaux",
      "Des communes",
      "Des centres communaux d'action sociale"
    ],
    narration: "Les collectivités territoriales jouent un rôle majeur : conseils départementaux, communes et centres communaux d'action sociale." },

  // ===== SECTION 5 — Les étapes de construction d'un partenariat =====
  // SLIDE 141
  { type: 'section', title: "5. Les étapes de construction d'un partenariat",
    narration: "Les étapes de construction d'un partenariat." },
  // SLIDE 142 — L'information mutuelle
  { type: 'content', title: "L'information mutuelle",
    intro: "La première étape consiste à établir un contact entre les institutions.",
    intro2: "Elle permet :",
    points: [
      "De mieux connaître les missions de chacun",
      "De partager des informations sur les publics accompagnés",
      "D'identifier les complémentarités"
    ],
    narration: "L'information mutuelle consiste à établir un contact pour connaître les missions, partager des informations et identifier les complémentarités." },
  // SLIDE 143 — La coordination
  { type: 'content', title: "La coordination",
    intro: "La coordination correspond à la mise en place d'échanges réguliers entre les partenaires.",
    intro2: "Elle peut prendre la forme :",
    points: [
      "De réunions de coordination",
      "De synthèses interinstitutionnelles",
      "De groupes de travail"
    ],
    narration: "La coordination correspond à la mise en place d'échanges réguliers : réunions, synthèses interinstitutionnelles et groupes de travail." },
  // SLIDE 144 — La coopération
  { type: 'content', title: "La coopération",
    intro: "La coopération correspond à une collaboration plus structurée. Les partenaires travaillent ensemble pour atteindre un objectif commun.",
    intro2: "Exemples :",
    points: [
      "Accompagnement conjoint d'un usager",
      "Mise en place d'actions communes",
      "Partage de ressources"
    ],
    narration: "La coopération est une collaboration structurée : accompagnement conjoint, actions communes et partage de ressources." },

  // ===== SECTION 6 — La formalisation des relations partenariales =====
  // SLIDE 145
  { type: 'section', title: "6. La formalisation des relations partenariales",
    narration: "La formalisation des relations partenariales." },
  // SLIDE 146 — La formalisation
  { type: 'content', title: "La formalisation des relations",
    intro: "Lorsque la coopération devient régulière, il peut être utile de formaliser la relation.",
    intro2: "Cette formalisation permet :",
    points: [
      "De clarifier les responsabilités",
      "De sécuriser les échanges d'informations",
      "De structurer l'organisation du partenariat"
    ],
    narration: "La formalisation permet de clarifier les responsabilités, sécuriser les échanges et structurer l'organisation du partenariat." },

  // ===== SECTION 7 — Le cadre juridique du partenariat =====
  // SLIDE 147
  { type: 'section', title: "7. Le cadre juridique du partenariat",
    narration: "Le cadre juridique du partenariat." },
  // SLIDE 148 — Les conventions de partenariat
  { type: 'content', title: "Les conventions de partenariat",
    intro: "La convention de partenariat est un document écrit définissant :",
    points: [
      "Les objectifs de la collaboration",
      "Les engagements des partenaires",
      "Les modalités d'intervention"
    ],
    highlight: "Elle permet de sécuriser les relations entre les institutions.",
    narration: "La convention de partenariat définit les objectifs, les engagements et les modalités d'intervention." },
  // SLIDE 149 — Les protocoles
  { type: 'content', title: "Les protocoles",
    intro: "Les protocoles définissent les procédures à suivre dans certaines situations.",
    intro2: "Exemples :",
    points: [
      "Protocole de signalement",
      "Protocole de coordination des interventions"
    ],
    narration: "Les protocoles définissent les procédures à suivre : protocole de signalement, protocole de coordination des interventions." },
  // SLIDE 150 — Les réseaux de santé
  { type: 'content', title: "Les réseaux de santé",
    intro: "Dans certains domaines, des réseaux de santé peuvent être mis en place afin de coordonner les interventions des professionnels.",
    highlight: "Ils visent à améliorer la continuité des soins et de l'accompagnement.",
    narration: "Les réseaux de santé coordonnent les interventions et améliorent la continuité des soins." },
  // SLIDE 151 — Le CPOM
  { type: 'content', title: "Le CPOM",
    intro: "Le Contrat Pluriannuel d'Objectifs et de Moyens (CPOM) est un outil contractuel entre les établissements et les autorités de tarification.",
    intro2: "Il définit :",
    points: [
      "Les objectifs de l'établissement",
      "Les moyens financiers alloués",
      "Les indicateurs de suivi"
    ],
    highlight: "Le partenariat peut être intégré dans ces objectifs.",
    narration: "Le CPOM est un contrat entre établissements et autorités de tarification. Il définit les objectifs, les moyens financiers et les indicateurs de suivi." },

  // ===== SECTION 8 — La gestion des tensions et conflits =====
  // SLIDE 152
  { type: 'section', title: "8. La gestion des tensions et conflits interinstitutionnels",
    narration: "La gestion des tensions et conflits interinstitutionnels." },
  // SLIDE 153 — Les tensions partenariales
  { type: 'content', title: "Les tensions partenariales",
    intro: "Le travail partenarial peut parfois générer des tensions entre institutions.",
    intro2: "Ces tensions peuvent être liées :",
    points: [
      "À des divergences d'objectifs",
      "À des différences de culture professionnelle",
      "À des problèmes de communication"
    ],
    narration: "Les tensions partenariales peuvent être liées à des divergences d'objectifs, des différences de culture professionnelle ou des problèmes de communication." },
  // SLIDE 154 — La médiation interinstitutionnelle
  { type: 'content', title: "La médiation interinstitutionnelle",
    intro: "La médiation consiste à faciliter le dialogue entre les partenaires afin de trouver une solution acceptable pour tous.",
    intro2: "Le responsable d'unité peut jouer un rôle de médiateur en :",
    points: [
      "Favorisant l'écoute mutuelle",
      "Clarifiant les attentes de chacun",
      "Recherchant des compromis"
    ],
    narration: "La médiation facilite le dialogue entre partenaires. Le responsable d'unité peut favoriser l'écoute mutuelle, clarifier les attentes et rechercher des compromis." },
  // SLIDE 155 — La clarification des rôles
  { type: 'content', title: "La clarification des rôles et des missions",
    intro: "Les conflits sont souvent liés à une mauvaise définition des responsabilités.",
    intro2: "Il est donc important de :",
    points: [
      "Préciser les missions de chaque institution",
      "Définir les limites d'intervention",
      "Formaliser les modalités de coopération"
    ],
    narration: "Il est important de préciser les missions de chaque institution, définir les limites d'intervention et formaliser les modalités de coopération." },

  // ===== CONCLUSION DU CHAPITRE 4 =====
  // SLIDE 156 — Conclusion
  { type: 'content', title: "Conclusion du Chapitre 4",
    intro: "Le partenariat constitue un élément essentiel du pilotage d'une unité d'intervention sociale.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [
      "Identifier les acteurs du territoire",
      "Structurer des relations de coopération",
      "Formaliser les collaborations",
      "Gérer les tensions entre institutions"
    ],
    highlight: "Ces compétences permettent de garantir une meilleure coordination des interventions et d'améliorer la qualité de l'accompagnement des usagers.",
    narration: "Le partenariat est essentiel au pilotage. Le responsable d'unité doit identifier les acteurs, structurer la coopération, formaliser les collaborations et gérer les tensions." },
  // SLIDE 157 — À retenir
  { type: 'jurybox', title: "À retenir — Chapitre 4", points: [
    "Le partenariat est une coopération structurée autour d'objectifs communs",
    "Le réseau est informel, le partenariat est formalisé et engageant",
    "La cartographie identifie les acteurs du territoire (sanitaire, social, éducatif, judiciaire)",
    "La construction passe par : information → coordination → coopération → formalisation",
    "Le cadre juridique : conventions, protocoles, réseaux de santé et CPOM",
    "Le responsable d'unité est médiateur en cas de conflit partenarial"
  ], narration: "Points clés : partenariat structuré, cartographie, construction en 4 étapes, cadre juridique et médiation." },

  // ============================================
  // CHAPITRE 5 — IMPULSER UNE DYNAMIQUE DE TRAVAIL EN RÉSEAU
  // ============================================

  // SLIDE 158 — Transition Chapitre 5
  { type: 'section', title: "Chapitre 5", subtitle: "Impulser une dynamique de travail en réseau",
    narration: "Chapitre 5 : Impulser une dynamique de travail en réseau." },

  // SLIDE 159 — Introduction Chapitre 5
  { type: 'content', title: "Introduction",
    intro: "Dans le secteur social et médico-social, les problématiques rencontrées par les usagers sont souvent complexes et nécessitent l'intervention de plusieurs acteurs.",
    intro2: "Face à cette complexité, les institutions ne peuvent agir de manière isolée. Il devient nécessaire de développer des dynamiques de travail en réseau.",
    intro3: "Le travail en réseau vise à améliorer :",
    points: [
      "La continuité des parcours des usagers",
      "La coordination des interventions professionnelles",
      "La circulation de l'information",
      "La complémentarité des compétences"
    ],
    highlight: "Le responsable d'unité joue un rôle essentiel dans l'animation de ces réseaux. Il doit être capable de mobiliser les partenaires, d'organiser les échanges et de structurer les coopérations.",
    narration: "Le travail en réseau vise à améliorer la continuité des parcours, la coordination des interventions, la circulation de l'information et la complémentarité des compétences." },

  // SLIDE 160 — Objectifs pédagogiques du Chapitre 5
  { type: 'content', title: "Objectifs pédagogiques du Chapitre 5",
    intro: "À l'issue de ce module, le professionnel doit être capable de :",
    points: [
      "Comprendre les principes du travail en réseau",
      "Identifier les différents types de réseaux professionnels",
      "Mobiliser les acteurs du territoire autour d'objectifs communs",
      "Animer et coordonner un réseau de partenaires",
      "Utiliser des outils numériques facilitant la coopération"
    ],
    narration: "Les objectifs du Chapitre 5 : comprendre le travail en réseau, identifier les types de réseaux, mobiliser les acteurs, animer un réseau et utiliser les outils numériques." },

  // SLIDE 161 — Sommaire du Chapitre 5
  { type: 'sommaire', title: "Sommaire du Chapitre 5",
    sections: [
      { num: "1", title: "Les concepts du travail en réseau", targetSlide: 162 },
      { num: "2", title: "Les enjeux de la coordination territoriale", targetSlide: 165 },
      { num: "3", title: "Typologie des réseaux professionnels", targetSlide: 169 },
      { num: "4", title: "Le rôle du cadre dans l'animation d'un réseau", targetSlide: 174 },
      { num: "5", title: "La circulation de l'information entre partenaires", targetSlide: 177 },
      { num: "6", title: "Les outils de coordination et de coopération", targetSlide: 179 },
      { num: "7", title: "Les plateformes collaboratives et dossiers partagés", targetSlide: 182 },
      { num: "8", title: "La protection des données et la sécurité des échanges", targetSlide: 185 }
    ],
    narration: "Le Chapitre 5 couvre 8 thèmes : concepts du travail en réseau, enjeux territoriaux, typologie, rôle du cadre, circulation de l'information, outils de coordination, plateformes numériques et protection des données." },

  // ===== SECTION 1 — Les concepts du travail en réseau =====
  // SLIDE 162
  { type: 'section', title: "1. Les concepts du travail en réseau",
    narration: "Les concepts du travail en réseau." },
  // SLIDE 163 — Définition
  { type: 'content', title: "Définition du travail en réseau",
    intro: "Le travail en réseau peut être défini comme :",
    highlight: "Un mode d'organisation fondé sur la coopération entre différents acteurs autonomes qui partagent des objectifs communs.",
    intro2: "Dans un réseau, les partenaires restent indépendants mais coordonnent leurs actions afin d'améliorer l'efficacité de leurs interventions.",
    narration: "Le travail en réseau est un mode d'organisation fondé sur la coopération entre acteurs autonomes partageant des objectifs communs." },
  // SLIDE 164 — Les principes
  { type: 'content', title: "Les principes du travail en réseau",
    intro: "Le travail en réseau repose sur plusieurs principes :",
    points: [
      "La coopération entre acteurs",
      "La reconnaissance des compétences de chacun",
      "La circulation de l'information",
      "La recherche d'une complémentarité des actions"
    ],
    narration: "Le travail en réseau repose sur la coopération, la reconnaissance des compétences, la circulation de l'information et la complémentarité des actions." },

  // ===== SECTION 2 — Les enjeux de la coordination territoriale =====
  // SLIDE 165
  { type: 'section', title: "2. Les enjeux de la coordination territoriale",
    narration: "Les enjeux de la coordination territoriale." },
  // SLIDE 166 — La complexité des situations
  { type: 'content', title: "La complexité des situations",
    intro: "Les situations des usagers sont souvent multidimensionnelles.",
    intro2: "Par exemple :",
    points: [
      "Une personne en situation de handicap peut avoir besoin d'un accompagnement éducatif, médical et social",
      "Un jeune en difficulté peut nécessiter un suivi scolaire, éducatif et judiciaire"
    ],
    highlight: "Le réseau permet de mobiliser différents professionnels afin d'apporter une réponse globale.",
    narration: "Les situations des usagers sont multidimensionnelles. Le réseau permet de mobiliser différents professionnels pour une réponse globale." },
  // SLIDE 167 — La continuité des parcours
  { type: 'content', title: "La continuité des parcours",
    intro: "Le travail en réseau facilite la coordination entre les différentes institutions intervenant auprès d'une même personne.",
    intro2: "Il permet notamment de :",
    points: [
      "Éviter les ruptures dans l'accompagnement",
      "Assurer la continuité des interventions",
      "Favoriser les transitions entre services"
    ],
    narration: "Le travail en réseau facilite la coordination et permet d'éviter les ruptures, assurer la continuité et favoriser les transitions." },
  // SLIDE 168 — L'optimisation des ressources
  { type: 'content', title: "L'optimisation des ressources",
    intro: "Le réseau permet de mutualiser les compétences et les ressources disponibles sur un territoire.",
    intro2: "Il contribue à :",
    points: [
      "Améliorer l'efficacité des interventions",
      "Limiter les doublons",
      "Développer des actions collectives"
    ],
    narration: "Le réseau permet de mutualiser les compétences, limiter les doublons et développer des actions collectives." },

  // ===== SECTION 3 — Typologie des réseaux professionnels =====
  // SLIDE 169
  { type: 'section', title: "3. Typologie des réseaux professionnels",
    narration: "Typologie des réseaux professionnels." },
  // SLIDE 170 — Les réseaux institutionnels
  { type: 'content', title: "Les réseaux institutionnels",
    intro: "Ces réseaux sont organisés autour d'institutions publiques ou d'organismes officiels.",
    intro2: "Exemples :",
    points: [
      "Réseaux départementaux d'action sociale",
      "Dispositifs territoriaux de coordination",
      "Réseaux de santé"
    ],
    highlight: "Ils visent à structurer la coopération entre les acteurs d'un territoire.",
    narration: "Les réseaux institutionnels sont organisés autour d'institutions publiques pour structurer la coopération territoriale." },
  // SLIDE 171 — Les réseaux thématiques
  { type: 'content', title: "Les réseaux thématiques",
    intro: "Les réseaux thématiques regroupent des professionnels autour d'une problématique spécifique.",
    intro2: "Exemples :",
    points: [
      "Réseaux de lutte contre les violences",
      "Réseaux d'insertion professionnelle",
      "Réseaux de prévention des addictions"
    ],
    highlight: "Ces réseaux permettent de développer une expertise collective sur une thématique particulière.",
    narration: "Les réseaux thématiques regroupent des professionnels autour d'une problématique spécifique : violences, insertion, addictions." },
  // SLIDE 172 — Les réseaux ville-hôpital
  { type: 'content', title: "Les réseaux ville-hôpital",
    intro: "Les réseaux ville-hôpital visent à améliorer la coordination entre les professionnels du secteur sanitaire et ceux du secteur social.",
    intro2: "Ils permettent notamment :",
    points: [
      "D'améliorer le suivi des patients",
      "De faciliter la coordination entre médecins et travailleurs sociaux",
      "D'assurer la continuité des soins"
    ],
    narration: "Les réseaux ville-hôpital améliorent la coordination entre secteur sanitaire et social pour assurer la continuité des soins." },
  // SLIDE 173 — Les réseaux informels
  { type: 'content', title: "Les réseaux informels",
    intro: "Certains réseaux reposent sur des relations professionnelles construites progressivement entre les acteurs.",
    intro2: "Ces réseaux peuvent être fondés sur :",
    points: [
      "La connaissance mutuelle",
      "Les échanges réguliers",
      "La confiance entre professionnels"
    ],
    highlight: "Bien que moins formalisés, ces réseaux jouent souvent un rôle important dans la coordination des interventions.",
    narration: "Les réseaux informels reposent sur la connaissance mutuelle, les échanges réguliers et la confiance entre professionnels." },

  // ===== SECTION 4 — Le rôle du cadre dans l'animation =====
  // SLIDE 174
  { type: 'section', title: "4. Le rôle du cadre dans l'animation d'un réseau",
    narration: "Le rôle du cadre dans l'animation d'un réseau." },
  // SLIDE 175 — Le rôle du responsable d'unité
  { type: 'content', title: "Le responsable d'unité : un nœud de réseau",
    intro: "Dans une dynamique de réseau, le responsable d'unité occupe souvent une position stratégique.",
    highlight: "Il agit comme un nœud de réseau, c'est-à-dire un acteur facilitant les échanges entre les partenaires.",
    intro2: "Ses missions peuvent inclure :",
    points: [
      "Identifier les partenaires pertinents",
      "Organiser les rencontres entre acteurs",
      "Favoriser la circulation de l'information",
      "Soutenir la coopération entre institutions"
    ],
    narration: "Le responsable d'unité agit comme un nœud de réseau. Il identifie les partenaires, organise les rencontres et soutient la coopération." },
  // SLIDE 176 — La coordination des acteurs
  { type: 'content', title: "La coordination des acteurs",
    intro: "La coordination consiste à organiser les interventions des différents partenaires afin de garantir leur cohérence.",
    intro2: "Elle peut prendre plusieurs formes :",
    points: [
      "Réunions de coordination",
      "Groupes de travail interinstitutionnels",
      "Échanges réguliers entre professionnels"
    ],
    intro3: "Ces temps de rencontre permettent :",
    points2: [
      "De partager les informations",
      "D'analyser les situations complexes",
      "De définir des actions communes"
    ],
    narration: "La coordination organise les interventions via des réunions, groupes de travail et échanges réguliers." },

  // ===== SECTION 5 — La circulation de l'information =====
  // SLIDE 177
  { type: 'section', title: "5. La circulation de l'information entre partenaires",
    narration: "La circulation de l'information entre partenaires." },
  // SLIDE 178 — La circulation de l'information
  { type: 'content', title: "La circulation de l'information",
    intro: "Une bonne circulation de l'information constitue un élément essentiel du travail en réseau.",
    intro2: "Elle permet :",
    points: [
      "D'éviter les ruptures dans l'accompagnement",
      "D'améliorer la coordination des actions",
      "De faciliter la prise de décision"
    ],
    highlight: "Le responsable d'unité doit veiller à organiser cette circulation tout en respectant le cadre légal relatif au secret professionnel et à la protection des données.",
    narration: "La circulation de l'information permet d'éviter les ruptures, améliorer la coordination et faciliter la prise de décision." },

  // ===== SECTION 6 — Les outils de coordination et de coopération =====
  // SLIDE 179
  { type: 'section', title: "6. Les outils de coordination et de coopération",
    narration: "Les outils de coordination et de coopération." },
  // SLIDE 180 — Les outils organisationnels
  { type: 'content', title: "Les outils organisationnels de coordination",
    intro: "Le responsable d'unité dispose de plusieurs outils pour organiser la coopération :",
    points: [
      "Les réunions de coordination interinstitutionnelles",
      "Les synthèses partenariales (bilans partagés des situations)",
      "Les protocoles de transmission d'informations",
      "Les fiches de liaison entre services",
      "Les annuaires partagés des ressources territoriales"
    ],
    highlight: "Ces outils structurent les échanges et garantissent une traçabilité des décisions prises en partenariat.",
    narration: "Les outils organisationnels incluent les réunions de coordination, les synthèses partenariales, les protocoles de transmission, les fiches de liaison et les annuaires partagés." },
  // SLIDE 181 — Tableau synthétique des outils
  { type: 'table', title: "Les principaux outils de coordination", rows: [
    { situation: "Réunion de coordination", besoin: "Échanges réguliers entre partenaires sur les situations suivies" },
    { situation: "Synthèse interinstitutionnelle", besoin: "Bilan partagé d'une situation complexe avec plusieurs acteurs" },
    { situation: "Fiche de liaison", besoin: "Transmission écrite d'informations entre services" },
    { situation: "Protocole d'intervention", besoin: "Procédure formalisée pour des situations spécifiques" },
    { situation: "Annuaire territorial", besoin: "Répertoire des partenaires et de leurs missions" }
  ], narration: "Les principaux outils : réunions, synthèses, fiches de liaison, protocoles et annuaires territoriaux." },

  // ===== SECTION 7 — Les plateformes collaboratives et dossiers partagés =====
  // SLIDE 182
  { type: 'section', title: "7. Les plateformes collaboratives et dossiers partagés",
    narration: "Les plateformes collaboratives et dossiers partagés." },
  // SLIDE 183 — Les plateformes collaboratives
  { type: 'content', title: "Les plateformes collaboratives",
    intro: "Les plateformes collaboratives permettent aux professionnels de partager des informations et de travailler ensemble à distance.",
    intro2: "Elles peuvent inclure :",
    points: [
      "Des espaces de travail partagés",
      "Des outils de gestion de projets",
      "Des forums de discussion"
    ],
    highlight: "Ces outils facilitent la coordination des actions et le suivi des projets.",
    narration: "Les plateformes collaboratives incluent des espaces partagés, des outils de gestion de projets et des forums de discussion." },
  // SLIDE 184 — Les dossiers partagés
  { type: 'content', title: "Les dossiers partagés",
    intro: "Dans certains domaines, des systèmes de dossiers partagés permettent aux professionnels d'accéder aux informations relatives aux usagers.",
    intro2: "Ces dispositifs favorisent :",
    points: [
      "La continuité de l'accompagnement",
      "La coordination entre professionnels",
      "La traçabilité des interventions"
    ],
    narration: "Les dossiers partagés favorisent la continuité de l'accompagnement, la coordination et la traçabilité des interventions." },

  // ===== SECTION 8 — La protection des données et la sécurité =====
  // SLIDE 185
  { type: 'section', title: "8. La protection des données et la sécurité des échanges",
    narration: "La protection des données et la sécurité des échanges." },
  // SLIDE 186 — La sécurité des données
  { type: 'content', title: "La sécurité des données",
    intro: "Le partage d'informations entre professionnels doit respecter les règles relatives à la protection des données personnelles.",
    intro2: "En France, ces règles sont encadrées par :",
    points: [
      "Le Règlement Général sur la Protection des Données (RGPD)",
      "Le secret professionnel"
    ],
    intro3: "Les professionnels doivent donc veiller à :",
    points2: [
      "Limiter l'accès aux informations aux personnes habilitées",
      "Garantir la confidentialité des données",
      "Sécuriser les outils numériques utilisés"
    ],
    narration: "Le partage d'informations doit respecter le RGPD et le secret professionnel. Les professionnels doivent limiter l'accès, garantir la confidentialité et sécuriser les outils." },
  // SLIDE 187 — Les bonnes pratiques numériques
  { type: 'content', title: "Les bonnes pratiques numériques",
    intro: "Pour garantir la sécurité des échanges, il est recommandé de :",
    points: [
      "Utiliser des plateformes sécurisées",
      "Limiter le partage d'informations sensibles",
      "Sensibiliser les professionnels à la protection des données",
      "Mettre en place des chartes d'utilisation des outils numériques",
      "Former les équipes aux enjeux de la cybersécurité"
    ],
    narration: "Les bonnes pratiques : plateformes sécurisées, limitation du partage d'informations sensibles, sensibilisation et formation des équipes." },

  // ===== CONCLUSION DU CHAPITRE 5 =====
  // SLIDE 188 — Conclusion
  { type: 'content', title: "Conclusion du Chapitre 5",
    intro: "Le travail en réseau constitue aujourd'hui un élément essentiel de l'action sociale et médico-sociale.",
    intro2: "Il permet :",
    points: [
      "De mobiliser les ressources du territoire",
      "De coordonner les interventions professionnelles",
      "D'améliorer la qualité de l'accompagnement"
    ],
    highlight: "Le responsable d'unité joue un rôle clé dans l'animation de ces réseaux. Il doit être capable de mobiliser les acteurs, de structurer les échanges et d'utiliser les outils numériques facilitant la coopération.",
    narration: "Le travail en réseau est essentiel. Le responsable d'unité doit mobiliser les acteurs, structurer les échanges et utiliser les outils numériques." },
  // SLIDE 189 — À retenir
  { type: 'jurybox', title: "À retenir — Chapitre 5", points: [
    "Le travail en réseau repose sur la coopération entre acteurs autonomes",
    "4 types de réseaux : institutionnels, thématiques, ville-hôpital, informels",
    "Le responsable d'unité est un animateur clé (nœud de réseau)",
    "La circulation de l'information est essentielle (secret professionnel à respecter)",
    "Outils : réunions, fiches de liaison, plateformes collaboratives, dossiers partagés",
    "Le RGPD encadre la protection des données et la sécurité des échanges"
  ], narration: "Points clés : coopération entre acteurs autonomes, 4 types de réseaux, rôle d'animateur du cadre, circulation de l'information, outils de coordination et respect du RGPD." },

  // ============================================
  // CHAPITRE 6 — COMMUNIQUER DANS LE CADRE DU PILOTAGE
  // ============================================

  // SLIDE 190 — Transition Chapitre 6
  { type: 'section', title: "Chapitre 6", subtitle: "Communiquer dans le cadre du pilotage",
    narration: "Chapitre 6 : Communiquer dans le cadre du pilotage." },

  // SLIDE 191 — Introduction Chapitre 6
  { type: 'content', title: "Introduction",
    intro: "La communication constitue une compétence essentielle pour le responsable d'une unité d'intervention sociale. En tant que cadre intermédiaire, il occupe une position stratégique au sein de l'organisation.",
    intro2: "La communication ne se limite pas à la transmission d'informations. Elle constitue un outil de pilotage et de management permettant :",
    points: [
      "De mobiliser les équipes autour des objectifs du service",
      "De favoriser la coopération entre professionnels",
      "De prévenir les tensions et les conflits",
      "D'accompagner les changements organisationnels"
    ],
    highlight: "Le responsable d'unité doit développer des compétences en communication professionnelle afin de garantir la qualité des échanges et la cohérence des actions menées au sein du service.",
    narration: "La communication est un outil de pilotage et de management. Le responsable d'unité doit mobiliser les équipes, favoriser la coopération, prévenir les tensions et accompagner les changements." },

  // SLIDE 192 — Objectifs pédagogiques du Chapitre 6
  { type: 'content', title: "Objectifs pédagogiques du Chapitre 6",
    intro: "À l'issue de ce module, le professionnel doit être capable de :",
    points: [
      "Adopter une posture de communication adaptée à sa fonction d'encadrement",
      "Utiliser les techniques d'écoute active et d'affirmation de soi",
      "Organiser et animer des réunions de pilotage efficaces",
      "Structurer la circulation de l'information dans l'équipe",
      "Gérer la communication dans des situations de crise"
    ],
    narration: "Les objectifs du Chapitre 6 : posture de communication, écoute active, animation de réunions, circulation de l'information et communication de crise." },

  // SLIDE 193 — Sommaire du Chapitre 6
  { type: 'sommaire', title: "Sommaire du Chapitre 6",
    sections: [
      { num: "1", title: "Les principes de la communication professionnelle", targetSlide: 194 },
      { num: "2", title: "La posture du cadre et la communication managériale", targetSlide: 197 },
      { num: "3", title: "L'écoute active et la communication assertive", targetSlide: 200 },
      { num: "4", title: "La circulation de l'information dans une équipe", targetSlide: 204 },
      { num: "5", title: "Les réunions de pilotage : préparation et animation", targetSlide: 207 },
      { num: "6", title: "Le compte-rendu et le suivi des décisions", targetSlide: 212 },
      { num: "7", title: "Les outils de communication interne", targetSlide: 215 },
      { num: "8", title: "La communication de crise et la gestion de situations sensibles", targetSlide: 218 }
    ],
    narration: "Le Chapitre 6 couvre 8 thèmes : communication professionnelle, posture du cadre, écoute active, circulation de l'information, réunions de pilotage, comptes-rendus, outils de communication et communication de crise." },

  // ===== SECTION 1 — Les principes de la communication professionnelle =====
  // SLIDE 194
  { type: 'section', title: "1. Les principes de la communication professionnelle",
    narration: "Les principes de la communication professionnelle." },
  // SLIDE 195 — Définition
  { type: 'content', title: "La communication professionnelle — Définition",
    intro: "La communication professionnelle peut être définie comme :",
    highlight: "L'ensemble des échanges d'informations, d'idées et de représentations entre les acteurs d'une organisation dans le cadre de leur activité professionnelle.",
    intro2: "Dans le secteur social et médico-social, la communication constitue un élément central du fonctionnement des équipes et de la qualité de l'accompagnement.",
    narration: "La communication professionnelle est l'ensemble des échanges entre les acteurs d'une organisation. Elle est centrale dans le fonctionnement des équipes." },
  // SLIDE 196 — Ce que permet la communication
  { type: 'content', title: "Les fonctions de la communication professionnelle",
    intro: "Elle permet notamment :",
    points: [
      "De partager les informations relatives aux usagers",
      "De coordonner les interventions professionnelles",
      "De soutenir la coopération entre les membres de l'équipe",
      "De faciliter la prise de décision",
      "De transmettre les valeurs et la culture institutionnelle"
    ],
    narration: "La communication permet de partager les informations, coordonner les interventions, soutenir la coopération, faciliter la prise de décision et transmettre la culture institutionnelle." },

  // ===== SECTION 2 — La posture du cadre =====
  // SLIDE 197
  { type: 'section', title: "2. La posture du cadre et la communication managériale",
    narration: "La posture du cadre et la communication managériale." },
  // SLIDE 198 — Les spécificités
  { type: 'content', title: "Les spécificités de la communication du cadre",
    intro: "Le responsable d'unité doit adapter sa communication à sa position d'encadrement.",
    intro2: "Il doit notamment :",
    points: [
      "Transmettre les orientations de la direction",
      "Expliquer les décisions organisationnelles",
      "Accompagner les professionnels dans leur pratique",
      "Soutenir la dynamique d'équipe"
    ],
    intro3: "Sa communication doit donc être :",
    points2: [
      "Claire",
      "Cohérente",
      "Transparente",
      "Adaptée aux interlocuteurs"
    ],
    narration: "Le cadre doit transmettre les orientations, expliquer les décisions, accompagner les professionnels et soutenir la dynamique d'équipe." },
  // SLIDE 199 — La posture professionnelle
  { type: 'content', title: "La posture professionnelle",
    intro: "La posture professionnelle correspond à l'attitude adoptée par le professionnel dans ses interactions.",
    intro2: "Pour un responsable d'unité, cette posture repose sur plusieurs dimensions :",
    points: [
      "La crédibilité — inspirer confiance et être reconnu comme légitime",
      "La cohérence — les paroles et les actes doivent être alignés",
      "La neutralité — adopter une position équilibrée pour préserver les relations",
      "L'exemplarité — incarner les valeurs défendues par l'institution"
    ],
    narration: "La posture du cadre repose sur la crédibilité, la cohérence, la neutralité et l'exemplarité." },

  // ===== SECTION 3 — L'écoute active et la communication assertive =====
  // SLIDE 200
  { type: 'section', title: "3. L'écoute active et la communication assertive",
    narration: "L'écoute active et la communication assertive." },
  // SLIDE 201 — L'écoute active et Carl Rogers
  { type: 'content', title: "L'écoute active — Carl Rogers",
    intro: "L'écoute active est une technique de communication développée par le psychologue américain Carl Rogers dans le cadre de son approche centrée sur la personne (ACP).",
    highlight: "Pour Rogers, l'écoute active repose sur trois attitudes fondamentales : l'empathie, la congruence (authenticité) et le regard positif inconditionnel.",
    intro2: "Ces principes, initialement conçus pour la relation d'aide, sont aujourd'hui largement utilisés dans le management et la communication professionnelle.",
    narration: "L'écoute active a été développée par Carl Rogers. Elle repose sur trois attitudes : l'empathie, la congruence et le regard positif inconditionnel." },
  // SLIDE 202 — Les techniques de l'écoute active
  { type: 'content', title: "Les techniques de l'écoute active",
    intro: "L'écoute active repose sur plusieurs techniques :",
    points: [
      "Porter une attention réelle à l'interlocuteur (présence physique et mentale)",
      "Reformuler les propos entendus pour vérifier la compréhension",
      "Poser des questions ouvertes pour approfondir l'échange",
      "Utiliser le silence comme outil de réflexion",
      "Éviter les jugements et les interprétations hâtives"
    ],
    intro2: "L'écoute active permet de :",
    points2: [
      "Mieux comprendre les difficultés exprimées par les professionnels",
      "Favoriser l'expression des points de vue",
      "Prévenir les tensions et désamorcer les conflits"
    ],
    narration: "Les techniques : attention réelle, reformulation, questions ouvertes, silence et non-jugement. Elle permet de comprendre les difficultés et prévenir les tensions." },
  // SLIDE 203 — L'affirmation de soi
  { type: 'content', title: "L'affirmation de soi — La communication assertive",
    intro: "L'affirmation de soi correspond à la capacité à exprimer ses opinions et ses décisions de manière claire et respectueuse.",
    intro2: "Elle se distingue :",
    points: [
      "De la passivité — ne pas exprimer son point de vue, subir les situations",
      "De l'agressivité — imposer son point de vue sans considération pour l'autre",
      "De la manipulation — influencer l'autre de manière détournée"
    ],
    intro3: "Une communication assertive permet de :",
    points2: [
      "Clarifier les attentes",
      "Poser un cadre professionnel",
      "Renforcer la confiance au sein de l'équipe",
      "Affirmer ses positions tout en respectant celles des autres"
    ],
    narration: "L'assertivité se distingue de la passivité, de l'agressivité et de la manipulation. Elle permet de clarifier les attentes et renforcer la confiance." },
  // SLIDE 204 — Écoute active et affirmation de soi (tableau comparatif)
  { type: 'comparison', title: "Écoute active et affirmation de soi",
    wrong: [
      { label: "Passivité : ne pas exprimer son point de vue", correct: "Assertivité : exprimer ses opinions clairement et respectueusement" },
      { label: "Agressivité : imposer son point de vue", correct: "Écoute active : reformuler, vérifier la compréhension, éviter les jugements" },
      { label: "Écouter sans attention", correct: "Porter une attention réelle à l'interlocuteur" },
      { label: "Interrompre l'interlocuteur", correct: "Clarifier les attentes et poser un cadre professionnel" }
    ],
    highlight: "L'écoute active favorise la compréhension mutuelle. L'affirmation de soi (assertivité) permet de clarifier les attentes et renforcer la confiance au sein de l'équipe.",
    narration: "L'écoute active favorise la compréhension mutuelle. L'affirmation de soi permet de clarifier les attentes et renforcer la confiance au sein de l'équipe." },

  // ===== SECTION 4 — La circulation de l'information =====
  // SLIDE 204
  { type: 'section', title: "4. La circulation de l'information dans une équipe",
    narration: "La circulation de l'information dans une équipe." },
  // SLIDE 205 — Les flux d'information
  { type: 'content', title: "Les flux d'information dans une organisation",
    intro: "La circulation de l'information dans une équipe peut prendre plusieurs formes :",
    points: [
      "La communication descendante — de la direction vers les professionnels (consignes, orientations, décisions)",
      "La communication ascendante — des professionnels vers la direction (remontées de terrain, alertes, propositions)",
      "La communication transversale — entre professionnels d'un même niveau (échanges de pratiques, coordination)"
    ],
    highlight: "Le responsable d'unité doit veiller à l'équilibre entre ces trois flux pour garantir une circulation fluide et pertinente de l'information.",
    narration: "Les trois flux d'information : descendante, ascendante et transversale. Le responsable d'unité doit veiller à leur équilibre." },
  // SLIDE 206 — Les obstacles à la communication
  { type: 'content', title: "Les obstacles à la circulation de l'information",
    intro: "Plusieurs obstacles peuvent freiner la communication au sein d'une équipe :",
    points: [
      "La rétention d'information (volontaire ou involontaire)",
      "Les canaux de communication inadaptés",
      "La surcharge informationnelle",
      "Les conflits interpersonnels",
      "Le manque de temps dédié aux échanges"
    ],
    highlight: "Le responsable d'unité doit identifier ces obstacles et mettre en place des dispositifs favorisant une communication ouverte et régulière.",
    narration: "Les obstacles : rétention d'information, canaux inadaptés, surcharge, conflits et manque de temps. Le cadre doit les identifier et y remédier." },

  // ===== SECTION 5 — Les réunions de pilotage =====
  // SLIDE 207
  { type: 'section', title: "5. Les réunions de pilotage : préparation et animation",
    narration: "Les réunions de pilotage : préparation et animation." },
  // SLIDE 208 — Les types de réunions
  { type: 'table', title: "Les différents types de réunions", rows: [
    { situation: "Réunions d'équipe", besoin: "Échanger sur l'organisation du service et les situations rencontrées" },
    { situation: "Réunions de coordination", besoin: "Organiser la coopération entre professionnels et partenaires" },
    { situation: "Réunions de pilotage", besoin: "Gestion stratégique et organisationnelle du service" },
    { situation: "Réunions de synthèse", besoin: "Bilan partagé d'une situation complexe (projet personnalisé)" },
    { situation: "Réunions institutionnelles", besoin: "Transmission des orientations et décisions de la direction" }
  ], narration: "Les types de réunions : réunions d'équipe, de coordination, de pilotage, de synthèse et institutionnelles." },
  // SLIDE 209 — La préparation
  { type: 'content', title: "La préparation d'une réunion",
    intro: "La préparation constitue une étape essentielle pour garantir l'efficacité d'une réunion.",
    intro2: "Elle consiste notamment à :",
    points: [
      "Définir les objectifs de la réunion",
      "Identifier les participants",
      "Préparer l'ordre du jour",
      "Rassembler les informations nécessaires",
      "Prévoir la durée et le lieu"
    ],
    highlight: "Un ordre du jour clair permet de structurer les échanges et d'éviter les discussions dispersées.",
    narration: "La préparation consiste à définir les objectifs, identifier les participants, préparer l'ordre du jour et rassembler les informations." },
  // SLIDE 210 — L'animation
  { type: 'content', title: "L'animation de la réunion",
    intro: "L'animation de la réunion relève généralement du responsable d'unité.",
    intro2: "Il doit :",
    points: [
      "Présenter les objectifs de la réunion",
      "Réguler les échanges",
      "Favoriser la participation de chacun",
      "Synthétiser les discussions",
      "Respecter le temps imparti"
    ],
    highlight: "L'animateur doit également veiller à maintenir un climat de respect et d'écoute.",
    narration: "L'animateur présente les objectifs, régule les échanges, favorise la participation, synthétise les discussions et respecte le temps." },
  // SLIDE 211 — Les étapes d'une réunion (visuel)
  { type: 'method', title: "Les 4 étapes d'une réunion efficace", steps: [
    { label: "Préparation", desc: "Objectifs, ordre du jour, participants, informations nécessaires" },
    { label: "Animation", desc: "Introduction, régulation des échanges, participation de chacun" },
    { label: "Compte-rendu", desc: "Date, participants, points abordés, décisions, actions" },
    { label: "Suivi", desc: "Avancement des actions, relances, point de suivi ultérieur" }
  ], narration: "Les 4 étapes : préparation, animation, compte-rendu et suivi des décisions." },

  // ===== SECTION 6 — Le compte-rendu et le suivi des décisions =====
  // SLIDE 212
  { type: 'section', title: "6. Le compte-rendu et le suivi des décisions",
    narration: "Le compte-rendu et le suivi des décisions." },
  // SLIDE 213 — Le compte-rendu
  { type: 'content', title: "Le compte-rendu de réunion",
    intro: "Le compte-rendu permet de conserver une trace des échanges et des décisions prises.",
    intro2: "Il doit généralement préciser :",
    points: [
      "La date de la réunion",
      "Les participants",
      "Les points abordés",
      "Les décisions prises",
      "Les actions à réaliser et leurs responsables"
    ],
    highlight: "Ce document constitue un outil de suivi des décisions et de mémoire institutionnelle.",
    narration: "Le compte-rendu précise la date, les participants, les points abordés, les décisions prises et les actions à réaliser." },
  // SLIDE 214 — Le suivi des décisions
  { type: 'content', title: "Le suivi des décisions",
    intro: "Après la réunion, il est important de s'assurer que les décisions prises sont effectivement mises en œuvre.",
    intro2: "Le responsable d'unité peut notamment :",
    points: [
      "Suivre l'avancement des actions",
      "Relancer les professionnels concernés",
      "Organiser un point de suivi lors d'une réunion ultérieure",
      "Évaluer l'efficacité des décisions prises",
      "Ajuster les actions si nécessaire"
    ],
    narration: "Le suivi des décisions consiste à vérifier l'avancement des actions, relancer les professionnels et organiser des points de suivi." },

  // ===== SECTION 7 — Les outils de communication interne =====
  // SLIDE 215
  { type: 'section', title: "7. Les outils de communication interne",
    narration: "Les outils de communication interne." },
  // SLIDE 216 — Les supports écrits
  { type: 'content', title: "Les supports de communication interne",
    intro: "Le responsable d'unité dispose de nombreux outils pour structurer la communication :",
    points: [
      "Le cahier de transmission — continuité de l'information entre les équipes",
      "Les notes de service — transmission de consignes et d'informations officielles",
      "L'affichage — informations accessibles à tous les membres de l'équipe",
      "La messagerie électronique — échanges rapides et traçabilité",
      "Les réunions (formelles et informelles) — échanges directs et participatifs"
    ],
    narration: "Les outils : cahier de transmission, notes de service, affichage, messagerie électronique et réunions." },
  // SLIDE 217 — Tableau des outils
  { type: 'table', title: "Les outils de communication et leurs usages", rows: [
    { situation: "Cahier de transmission", besoin: "Continuité de l'information entre les équipes (relais jour/nuit)" },
    { situation: "Note de service", besoin: "Consignes officielles, décisions de la direction" },
    { situation: "Messagerie électronique", besoin: "Échanges rapides, diffusion d'informations, traçabilité" },
    { situation: "Tableau d'affichage", besoin: "Informations accessibles à tous (planning, procédures, actualités)" },
    { situation: "Entretien individuel", besoin: "Échanges personnalisés, suivi professionnel, accompagnement" }
  ], narration: "Les principaux outils : cahier de transmission, note de service, messagerie, affichage et entretiens individuels." },

  // ===== SECTION 8 — La communication de crise =====
  // SLIDE 218
  { type: 'section', title: "8. La communication de crise et la gestion de situations sensibles",
    narration: "La communication de crise et la gestion de situations sensibles." },
  // SLIDE 219 — Définition de la crise
  { type: 'content', title: "La communication de crise — Définition",
    intro: "Une crise peut être définie comme :",
    highlight: "Une situation exceptionnelle susceptible de perturber gravement le fonctionnement d'une organisation.",
    intro2: "Dans le secteur social et médico-social, les crises peuvent être liées :",
    points: [
      "À un incident concernant un usager",
      "À un événement grave au sein de l'établissement",
      "À une situation médiatique sensible",
      "À un conflit social ou une tension institutionnelle"
    ],
    narration: "Une crise est une situation exceptionnelle susceptible de perturber gravement le fonctionnement d'une organisation." },
  // SLIDE 220 — Les enjeux
  { type: 'content', title: "Les enjeux de la communication de crise",
    intro: "Dans ces situations, la communication doit permettre de :",
    points: [
      "Informer rapidement les acteurs concernés",
      "Éviter la propagation de rumeurs",
      "Maintenir la confiance des équipes et des partenaires",
      "Protéger les usagers et les professionnels",
      "Préserver l'image de l'institution"
    ],
    narration: "La communication de crise doit informer rapidement, éviter les rumeurs, maintenir la confiance et protéger les acteurs." },
  // SLIDE 221 — Les principes
  { type: 'content', title: "Les principes de la communication de crise",
    intro: "Une communication de crise efficace repose sur plusieurs principes :",
    points: [
      "La rapidité — transmettre les informations rapidement pour éviter les incertitudes",
      "La clarté — les messages doivent être simples et compréhensibles",
      "La cohérence — les informations diffusées doivent être alignées entre les acteurs",
      "La transparence — communiquer avec honnêteté sans dissimuler les faits",
      "La maîtrise — garder son calme et sa posture professionnelle"
    ],
    narration: "Les principes : rapidité, clarté, cohérence, transparence et maîtrise." },
  // SLIDE 222 — Les éléments de langage
  { type: 'content', title: "Les éléments de langage",
    intro: "Dans certaines situations sensibles, il peut être nécessaire de préparer des éléments de langage.",
    intro2: "Les éléments de langage correspondent à des formulations permettant de :",
    points: [
      "Expliquer la situation de manière factuelle",
      "Rassurer les interlocuteurs",
      "Transmettre des informations fiables",
      "Anticiper les questions fréquentes"
    ],
    highlight: "Ils permettent d'éviter les maladresses et les contradictions dans la communication.",
    narration: "Les éléments de langage permettent d'expliquer la situation, rassurer les interlocuteurs et transmettre des informations fiables." },

  // ===== CONCLUSION DU CHAPITRE 6 =====
  // SLIDE 223 — Conclusion
  { type: 'content', title: "Conclusion du Chapitre 6",
    intro: "La communication constitue un levier essentiel pour le pilotage d'une unité d'intervention sociale.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [
      "Adopter une posture professionnelle adaptée (Rogers : empathie, congruence, regard positif)",
      "Favoriser les échanges au sein de l'équipe",
      "Organiser et animer des réunions de pilotage",
      "Structurer la circulation de l'information (descendante, ascendante, transversale)",
      "Gérer les situations de crise avec rapidité, clarté et cohérence"
    ],
    highlight: "Ces compétences contribuent à renforcer la cohésion des équipes et à améliorer la qualité du fonctionnement du service.",
    narration: "La communication est un levier essentiel. Le cadre doit adopter une posture adaptée, organiser les réunions, structurer l'information et gérer les crises." },
  // SLIDE 224 — À retenir
  { type: 'jurybox', title: "À retenir — Chapitre 6", points: [
    "La communication professionnelle est un outil de pilotage (pas seulement un transfert d'info)",
    "Posture du cadre : crédibilité, cohérence, neutralité, exemplarité",
    "L'écoute active (Carl Rogers) : empathie, congruence, regard positif inconditionnel",
    "3 flux d'information : descendant, ascendant, transversal",
    "Les réunions : préparation → animation → compte-rendu → suivi",
    "Communication de crise : rapidité, clarté, cohérence, transparence"
  ], narration: "Points clés : communication comme outil de pilotage, posture du cadre, écoute active de Rogers, flux d'information, réunions structurées et communication de crise." },

  // SLIDE 225 — FÉLICITATIONS
  {
    type: 'congratulations',
    title: "Bloc 1 terminé !",
    message: "Vous avez parcouru l'ensemble des 6 chapitres du Bloc 1 CAFERUIS.",
    details: [
      "Chapitre 1 — Organiser la mise en œuvre de l'activité",
      "Chapitre 2 — Assurer la qualité des projets individuels et collectifs",
      "Chapitre 3 — Évaluer les résultats de la mise en œuvre de l'activité",
      "Chapitre 4 — Mettre en place un partenariat au service de l'activité",
      "Chapitre 5 — Impulser une dynamique de travail en réseau",
      "Chapitre 6 — Communiquer dans le cadre du pilotage",
      "Passez maintenant au Quiz BC1 pour tester vos connaissances !"
    ],
    narration: "Félicitations ! Vous avez parcouru les 6 chapitres du Bloc 1 CAFERUIS. Passez au quiz pour valider vos acquis."
  }
];

const CAFBC1Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved && saved.lastViewedSlide > 0) {
      setCurrentSlide(saved.lastViewedSlide);
      setViewedSlides(new Set(saved.viewedSlides || [0]));
    }
    markSlideViewed(ACTIVITY_ID, 0, slides.length);
  }, []);

  const goToSlide = (i) => {
    setCurrentSlide(i);
    setViewedSlides(prev => new Set([...prev, i]));
    markSlideViewed(ACTIVITY_ID, i, slides.length);
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      const next = currentSlide + 1;
      setCurrentSlide(next);
      setViewedSlides(prev => new Set([...prev, next]));
      markSlideViewed(ACTIVITY_ID, next, slides.length);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) setCurrentSlide(currentSlide - 1);
  };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setIsPlaying(false);
  };

  const getSlideText = (slide) => {
    if (slide.narration) return slide.narration;
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.points) text += '. ' + slide.points.join('. ');
    if (slide.highlight) text += '. ' + slide.highlight;
    return text;
  };

  const speakText = async (text) => {
    if (isPlaying) { stopAudio(); return; }
    if (!text || !text.trim()) { toast.info("Pas de contenu audio pour cette slide"); return; }
    let t = text.replace(/CAFERUIS/g, 'cafferui').replace(/UIS/g, "unité d'intervention sociale").replace(/HAS/g, 'haute autorité de santé').replace(/RBPP/g, 'recommandations de bonnes pratiques professionnelles').replace(/PDCA/g, 'plan do check act').replace(/CPOM/g, "contrat pluriannuel d'objectifs et de moyens").replace(/RGPD/g, 'règlement général sur la protection des données').replace(/CVS/g, 'conseil de la vie sociale');
    if (t.length > 800) t = t.substring(0, 800) + '...';
    try {
      setIsLoading(true); setIsPlaying(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: t, speed: 1.1 }) });
      if (!response.ok) throw new Error('TTS failed');
      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      setIsLoading(false);
      if (audioData) {
        const audioBlob = new Blob([Uint8Array.from(atob(audioData), c => c.charCodeAt(0))], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => { toast.error("Erreur de lecture audio"); setIsPlaying(false); };
        audioRef.current.play().catch(() => { toast.error("Impossible de lire l'audio"); setIsPlaying(false); });
      } else { toast.error("Pas de données audio reçues"); setIsPlaying(false); }
    } catch { toast.error("Erreur lors de la génération audio"); setIsPlaying(false); setIsLoading(false); }
  };

  useEffect(() => { return () => { if (audioRef.current) audioRef.current.pause(); }; }, []);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(26,122,109,0.55)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                <Shield size={30} className="text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg tracking-wide">{slide.title}</h1>
              <p className="text-lg md:text-xl text-white/90 mb-4 max-w-xl leading-relaxed drop-shadow">{slide.subtitle}</p>
              <div className="flex gap-2 flex-wrap justify-center">
                {['6 chapitres', `${slides.length} slides`, 'Audio inclus'].map((tag, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)' }}>{tag}</span>
                ))}
              </div>
              <p className="mt-4 text-xs text-white/60 max-w-lg">Certificat d'Aptitude aux Fonctions d'Encadrement et de Responsable d'Unité d'Intervention Sociale</p>
            </div>
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(245,240,232,0.75)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: 'linear-gradient(135deg, #1a7a6d 0%, #264653 100%)', padding: '0.6rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">{slide.title}</h2>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center px-4 py-3">
                <p className="text-center text-gray-500 text-xs mb-3">Cliquez sur un chapitre pour y accéder directement</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-4xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx}
                      className="flex items-center gap-2 p-2.5 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all backdrop-blur-sm"
                      style={{ border: '1px solid #e0e0e0' }}
                      onClick={() => goToSlide(s.targetSlide)}
                      data-testid={`sommaire-section-${idx}`}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ background: '#1a7a6d' }}>{s.num}</span>
                      <span className="text-gray-700 text-xs leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: '110% auto', backgroundPosition: 'center 95%', backgroundRepeat: 'no-repeat' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(26,122,109,0.45)' }} />
            <div className="relative z-10 text-center px-8">
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg mb-3">{slide.title}</h2>
              {slide.subtitle && (
                <p className="text-base md:text-lg text-white/90 max-w-xl mx-auto leading-relaxed drop-shadow" style={{ background: 'rgba(255,255,255,0.1)', padding: '0.5rem 1.2rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {slide.subtitle}
                </p>
              )}
            </div>
          </div>
        );

      case 'content':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-gray-700 text-[14px] leading-relaxed text-center mb-2">{slide.intro}</p>}
              {slide.intro2 && !slide.points2 && <p className="text-gray-700 text-[14px] leading-relaxed text-center mb-2">{slide.intro2}</p>}
              {slide.intro3 && <p className="text-gray-700 text-[14px] leading-relaxed text-center mb-3 font-medium">{slide.intro3}</p>}
              {slide.points && (
                <div className="space-y-1.5 mb-3">
                  {slide.points.map((pt, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white p-2 rounded-lg shadow-sm">
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.primary }} />
                      <span className="text-gray-700 text-[14px] leading-relaxed">{pt}</span>
                    </div>
                  ))}
                </div>
              )}
              {slide.points2 && (
                <>
                  {slide.intro2 && <p className="text-gray-700 text-[14px] leading-relaxed text-center mb-2 font-medium">{slide.intro2}</p>}
                  <div className="space-y-1.5 mb-3">
                    {slide.points2.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2 bg-white p-2 rounded-lg shadow-sm">
                        <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.secondary }} />
                        <span className="text-gray-700 text-[14px] leading-relaxed">{pt}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {slide.highlight && (
                <div className="p-3 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.highlight}
                </div>
              )}
            </div>
          </div>
        );

      case 'grid':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(245,240,232,0.8)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: 'linear-gradient(135deg, #1a7a6d 0%, #264653 100%)', padding: '0.6rem 0' }}>
                <h2 className="text-base font-bold text-center text-white tracking-wide">{slide.title}</h2>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center px-4 py-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-4xl mx-auto">
                  {slide.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2.5 bg-white/90 rounded-lg shadow-sm backdrop-blur-sm" style={{ border: '1px solid #e0e0e0' }}>
                      <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{item.num}</span>
                      <span className="text-gray-700 text-xs leading-tight font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <div className="p-2.5 rounded-t-lg text-white font-bold text-sm text-center" style={{ background: theme.primary }}>{slide.title}</div>
              <div className="bg-white rounded-b-lg shadow-md overflow-hidden">
                {slide.rows.map((row, i) => (
                  <div key={i} className="flex border-b last:border-0" style={{ borderColor: theme.primary + '15' }}>
                    <div className="w-2/5 p-2.5 font-semibold text-[13px] flex items-center" style={{ color: theme.primary, background: theme.primary + '08' }}>
                      {row.situation}
                    </div>
                    <div className="w-3/5 p-2.5 text-gray-700 text-[13px] leading-relaxed">
                      {row.besoin}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'method':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-2 mb-3">
                {slide.steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm" style={{ borderLeft: `3px solid ${theme.primary}` }}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{i + 1}</span>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: theme.text }}>{step.label}</p>
                      <p className="text-gray-600 text-[13px] leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              {slide.quote && (
                <div className="p-3 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.quote}
                </div>
              )}
            </div>
          </div>
        );

      case 'orgchart':
        return (
          <div className="p-4 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="flex flex-col items-center mb-3">
                {/* Direction */}
                <div className="px-5 py-2 rounded-lg text-white font-bold text-sm shadow-md" style={{ background: 'linear-gradient(135deg, #264653 0%, #1a7a6d 100%)' }}>Direction</div>
                <div className="w-0.5 h-4" style={{ background: theme.primary }} />
                {/* Responsable d'unité */}
                <div className="px-5 py-2 rounded-lg text-white font-bold text-sm shadow-md" style={{ background: theme.primary }}>Responsable d'unité</div>
                <div className="w-0.5 h-4" style={{ background: theme.primary }} />
                {/* Branche */}
                <div className="flex items-start">
                  <div className="flex flex-col items-center">
                    <div className="h-0.5 w-32" style={{ background: theme.primary }} />
                  </div>
                </div>
                <div className="flex gap-3 relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5" style={{ background: theme.primary, width: '75%' }} />
                  {['Éducateurs', 'Psychologue', 'Infirmier'].map((role, i) => (
                    <div key={i} className="flex flex-col items-center">
                      <div className="w-0.5 h-3" style={{ background: theme.primary }} />
                      <div className="px-3 py-1.5 rounded-lg text-white text-xs font-medium shadow-sm" style={{ background: theme.primary + 'cc' }}>{role}</div>
                    </div>
                  ))}
                </div>
                {/* AES sous Éducateurs */}
                <div className="flex items-start mt-0">
                  <div className="flex flex-col items-center" style={{ marginRight: '8.5rem' }}>
                    <div className="w-0.5 h-3" style={{ background: theme.primary }} />
                    <div className="px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm" style={{ background: theme.primary + '20', color: theme.primary, border: `1px solid ${theme.primary}40` }}>AES / Moniteurs éducateurs</div>
                  </div>
                </div>
              </div>
              <div className="p-2.5 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                Cet organigramme permet de comprendre :
                <div className="flex flex-wrap justify-center gap-3 mt-1 not-italic">
                  {slide.points.map((pt, i) => (
                    <span key={i} className="flex items-center gap-1 text-[12px]">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: theme.secondary }} />
                      {pt}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'orgchart_func':
        return (
          <div className="p-4 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-1 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-gray-600 text-[13px] text-center mb-3">{slide.intro}</p>}
              <div className="flex flex-col items-center mb-3">
                {/* Responsable d'unité */}
                <div className="px-5 py-2 rounded-lg text-white font-bold text-sm shadow-md" style={{ background: theme.primary }}>Responsable d'unité</div>
                <div className="w-0.5 h-4" style={{ background: theme.primary }} />
                {/* 3 branches fonctionnelles */}
                <div className="flex gap-4 relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5" style={{ background: theme.primary, width: '80%' }} />
                  {[
                    { func: 'Accompagnement éducatif', sub: 'Éducateurs\nAES' },
                    { func: 'Coordination clinique', sub: 'Psychologue\nInfirmier' },
                    { func: 'Suivi administratif et logistique', sub: 'Secrétariat' }
                  ].map((col, i) => (
                    <div key={i} className="flex flex-col items-center" style={{ minWidth: '130px' }}>
                      <div className="w-0.5 h-3" style={{ background: theme.primary }} />
                      <div className="px-3 py-1.5 rounded-lg text-white text-[11px] font-semibold shadow-sm text-center" style={{ background: 'linear-gradient(135deg, #264653 0%, #1a7a6d 100%)' }}>{col.func}</div>
                      <div className="w-0.5 h-3" style={{ background: theme.primary + '60' }} />
                      <div className="px-3 py-1 rounded-lg text-[11px] font-medium text-center whitespace-pre-line" style={{ background: theme.primary + '15', color: theme.primary, border: `1px solid ${theme.primary}30` }}>{col.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-2.5 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                Cet organigramme permet de mieux comprendre :
                <div className="flex flex-wrap justify-center gap-3 mt-1 not-italic">
                  {slide.points.map((pt, i) => (
                    <span key={i} className="flex items-center gap-1 text-[12px]">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: theme.secondary }} />
                      {pt}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'gantt':
        const ganttData = [
          { task: 'Analyse des besoins', start: 0, width: 25 },
          { task: 'Réunion équipe', start: 20, width: 20 },
          { task: 'Mise en place outils', start: 35, width: 35 },
          { task: 'Évaluation', start: 65, width: 30 }
        ];
        const months = ['Janv.', 'Févr.', 'Mars', 'Avril', 'Mai'];
        return (
          <div className="p-4 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-1 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-gray-600 text-[13px] text-center mb-3">{slide.intro}</p>}
              <div className="bg-white rounded-lg shadow-md p-4 mb-3">
                <p className="text-xs font-bold mb-3 text-center" style={{ color: theme.primary }}>Projet d'amélioration du service</p>
                <div className="flex mb-1 pl-32">
                  {months.map((m, i) => (
                    <div key={i} className="flex-1 text-[10px] text-gray-400 text-center">{m}</div>
                  ))}
                </div>
                <div className="space-y-2">
                  {ganttData.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-gray-700 w-32 text-right flex-shrink-0">{item.task}</span>
                      <div className="flex-1 h-6 rounded relative" style={{ background: theme.primary + '10' }}>
                        <div className="absolute h-full rounded-md shadow-sm" style={{
                          left: `${item.start}%`,
                          width: `${item.width}%`,
                          background: `linear-gradient(90deg, ${theme.primary}${i % 2 === 0 ? 'cc' : ''}, ${theme.primary})`,
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {slide.highlight && (
                <div className="p-2.5 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.highlight}
                </div>
              )}
            </div>
          </div>
        );

      case 'flux_schema':
        return (
          <div className="p-4 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-1 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-gray-600 text-[13px] text-center mb-2">{slide.intro}</p>}
              <div className="flex gap-4 items-start">
                {/* Schéma des flux */}
                <div className="bg-white rounded-lg shadow-md p-3 flex-shrink-0" style={{ width: '220px' }}>
                  <p className="text-[10px] font-bold text-center mb-2" style={{ color: theme.primary }}>Schéma des flux</p>
                  <div className="flex flex-col items-center">
                    <div className="px-3 py-1 rounded text-white text-[10px] font-bold" style={{ background: '#264653' }}>Direction</div>
                    <div className="flex items-center gap-1">
                      <span className="text-[8px] italic" style={{ color: theme.primary }}>flux descendants</span>
                      <span style={{ color: theme.primary }}>↓</span>
                    </div>
                    <div className="px-3 py-1 rounded text-white text-[10px] font-bold" style={{ background: theme.primary }}>Responsable d'unité</div>
                    <div className="flex w-full justify-between items-center px-1">
                      <div className="flex flex-col items-center">
                        <span style={{ color: theme.secondary }}>↑</span>
                        <span className="text-[8px] italic" style={{ color: theme.secondary }}>ascendants</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span style={{ color: theme.primary }}>↓</span>
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded text-white text-[10px] font-bold" style={{ background: theme.primary + 'cc' }}>Équipe éducative</div>
                    <div className="flex items-center gap-1">
                      <span className="text-[8px] italic text-amber-600">flux transversaux</span>
                      <span className="text-amber-600">↕</span>
                    </div>
                    <div className="px-3 py-1 rounded text-[10px] font-medium" style={{ background: theme.primary + '15', color: theme.primary, border: `1px solid ${theme.primary}30` }}>Professionnels / partenaires</div>
                  </div>
                </div>
                {/* Points */}
                <div className="flex-1">
                  {slide.intro2 && <p className="text-gray-700 text-[13px] mb-2 font-medium">{slide.intro2}</p>}
                  <div className="space-y-1.5">
                    {slide.points.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2 bg-white p-2 rounded-lg shadow-sm">
                        <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: theme.primary }} />
                        <span className="text-gray-700 text-[13px]">{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'dashboard':
        return (
          <div className="p-4 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-1 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-gray-600 text-[13px] text-center mb-3">{slide.intro}</p>}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white rounded-lg shadow-sm p-3" style={{ borderTop: `3px solid ${theme.primary}` }}>
                  <p className="text-xs font-bold mb-2 text-center" style={{ color: theme.primary }}>Indicateurs quantitatifs</p>
                  <div className="space-y-1.5">
                    {slide.quantitatifs.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: theme.primary }} />
                        <span className="text-gray-700 text-[12px]">{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-3" style={{ borderTop: `3px solid ${theme.secondary}` }}>
                  <p className="text-xs font-bold mb-2 text-center" style={{ color: theme.secondary }}>Indicateurs qualitatifs</p>
                  <div className="space-y-1.5">
                    {slide.qualitatifs.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: theme.secondary }} />
                        <span className="text-gray-700 text-[12px]">{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {slide.highlight && (
                <div className="p-2.5 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.highlight}
                </div>
              )}
            </div>
          </div>
        );

      case 'eisenhower':
        return (
          <div className="p-4 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="flex gap-4 items-start">
                {/* Matrice visuelle */}
                <div className="flex-1">
                  <div className="relative">
                    {/* Labels axes */}
                    <div className="flex justify-center mb-1">
                      <span className="text-xs font-bold tracking-widest" style={{ color: theme.primary }}>IMPORTANT</span>
                    </div>
                    <div className="flex">
                      <div className="flex items-center pr-2">
                        <span className="text-xs font-bold tracking-widest" style={{ color: theme.secondary, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>URGENT</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 flex-1">
                        <div className="p-3 rounded-tl-lg text-center" style={{ background: '#dc262615', border: '1px solid #dc262630' }}>
                          <p className="text-[11px] font-bold text-red-700">FAIRE</p>
                          <p className="text-[10px] text-red-600">immédiatement</p>
                        </div>
                        <div className="p-3 rounded-tr-lg text-center" style={{ background: '#f59e0b15', border: '1px solid #f59e0b30' }}>
                          <p className="text-[11px] font-bold text-amber-700">DÉLÉGUER</p>
                          <p className="text-[10px] text-amber-600">si possible</p>
                        </div>
                        <div className="p-3 rounded-bl-lg text-center" style={{ background: theme.primary + '15', border: `1px solid ${theme.primary}30` }}>
                          <p className="text-[11px] font-bold" style={{ color: theme.primary }}>PLANIFIER</p>
                          <p className="text-[10px] text-teal-600">et organiser</p>
                        </div>
                        <div className="p-3 rounded-br-lg text-center" style={{ background: '#6b728015', border: '1px solid #6b728030' }}>
                          <p className="text-[11px] font-bold text-gray-600">ÉLIMINER</p>
                          <p className="text-[10px] text-gray-500">ou reporter</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Texte */}
                <div className="flex-1 flex flex-col justify-center">
                  <p className="text-gray-700 text-[13px] mb-2 font-medium">Cette matrice aide à distinguer :</p>
                  <div className="space-y-1.5 mb-3">
                    {slide.points.map((pt, i) => (
                      <div key={i} className="flex items-start gap-2 bg-white p-2 rounded-lg shadow-sm">
                        <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: theme.primary }} />
                        <span className="text-gray-700 text-[13px]">{pt}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-2.5 rounded-lg text-[12px] italic" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                    Le responsable d'unité doit prioriser les actions en distinguant l'urgent de l'important.
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'comparison':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-2 mb-3">
                {slide.wrong.map((item, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 rounded-lg text-[13px] bg-red-50 border border-red-200">
                      <span className="text-red-700">{item.label}</span>
                    </div>
                    <div className="p-2.5 rounded-lg text-[13px] bg-green-50 border border-green-200">
                      <span className="text-green-700">{item.correct}</span>
                    </div>
                  </div>
                ))}
              </div>
              {slide.highlight && (
                <div className="p-3 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.highlight}
                </div>
              )}
            </div>
          </div>
        );

      case 'action_plan_table':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="rounded-xl overflow-hidden shadow-lg border" style={{ borderColor: theme.primary + '30' }}>
                <div className="grid grid-cols-4" style={{ background: theme.primary }}>
                  {slide.headers.map((h, i) => (
                    <div key={i} className="p-3 text-white font-bold text-[13px] text-center border-r last:border-r-0 border-white/20">{h}</div>
                  ))}
                </div>
                {slide.data.map((row, i) => (
                  <div key={i} className="grid grid-cols-4 border-b last:border-b-0" style={{ borderColor: theme.primary + '15', background: i % 2 === 0 ? 'white' : theme.primary + '06' }}>
                    {row.map((cell, j) => (
                      <div key={j} className="p-3 text-[13px] text-gray-700 border-r last:border-r-0 flex items-center" style={{ borderColor: theme.primary + '15' }}>
                        {j === 0 ? <span className="font-semibold" style={{ color: theme.primary }}>{cell}</span> : cell}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {slide.quote && (
                <div className="p-3 mt-3 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.quote}
                </div>
              )}
            </div>
          </div>
        );

      case 'indicator_table':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="rounded-xl overflow-hidden shadow-lg border" style={{ borderColor: theme.primary + '30' }}>
                <div className="grid grid-cols-2" style={{ background: theme.primary }}>
                  {slide.headers.map((h, i) => (
                    <div key={i} className="p-3 text-white font-bold text-sm text-center border-r last:border-r-0 border-white/20">{h}</div>
                  ))}
                </div>
                {slide.data.map((row, i) => (
                  <div key={i} className="grid grid-cols-2 border-b last:border-b-0" style={{ borderColor: theme.primary + '15', background: i % 2 === 0 ? 'white' : theme.primary + '06' }}>
                    <div className="p-3 font-semibold text-[13px] border-r flex items-center gap-2" style={{ color: theme.primary, borderColor: theme.primary + '15' }}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: theme.secondary }}></span>
                      {row.domaine}
                    </div>
                    <div className="p-3 text-[13px] text-gray-700 flex items-center">{row.indicateur}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'jurybox':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl mx-auto w-full">
              <div className="p-2.5 rounded-t-lg text-white font-bold text-sm text-center" style={{ background: theme.primary }}>{slide.title}</div>
              <div className="bg-white rounded-b-lg shadow-md p-4 space-y-2">
                {slide.intro && <p className="text-gray-700 text-[14px] font-medium mb-1">{slide.intro}</p>}
                {slide.points.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 border-b last:border-0" style={{ borderColor: theme.primary + '20' }}>
                    <Sparkles size={14} className="mt-0.5 flex-shrink-0" style={{ color: theme.secondary }} />
                    <span className="text-gray-700 text-[14px]">{item}</span>
                  </div>
                ))}
                {slide.highlight && (
                  <div className="p-2.5 mt-2 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                    {slide.highlight}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(26,122,109,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">{slide.message}</p>
              <div className="space-y-1 w-full mb-3">
                {slide.details.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
                    <Shield size={12} className="text-white flex-shrink-0" />
                    <span className="text-white text-[13px]">{d}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-xl w-full" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-sm font-semibold" style={{ color: theme.primary }}>
                  Rendez-vous dans le Quiz BC1 pour valider vos acquis !
                </p>
              </div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="caferuis-bc1-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bc1-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bc1-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bc1-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default CAFBC1Presentation;
